import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  FormControl,
  Grid,
  IconButton,
  makeStyles,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
// BCM-881: filled ⓘ used on the dashboard headings and quantity column headers.
import InfoIcon from "@material-ui/icons/Info";
import SearchIcon from "@material-ui/icons/Search";
import React, { useEffect, useState, useRef } from "react";
import moment from "moment";
// import FlagIcon from "@mui/icons-material/Flag";
import FlagIcon from "@material-ui/icons/Flag";
import {
  Add,
  Refresh,
} from "@material-ui/icons";
import InventoryStatusPopup from "./InventoryStatusPopup";
import { ChevronLeft, ChevronRight } from "@material-ui/icons";
import jwtDecode from "jwt-decode";
import {
  fetchAllInventoryData,
  fetchAllLotnumbers,
  fetchAllReceivingPlantIDs,
  fetchAllRMPMCodes,
  fetchAllStorageLocationIds,
  fetchCMOMembers,
} from "../../../containers/actions/common/inventoryStatus";
import { saveInventoryStatusFilters } from "../../../containers/actions/common/inventoryStatusActions";
import { FETCH_ALL_INVENTORY_DATA_LOADING } from "../../../containers/actions/types";
import { useDispatch, useSelector } from "react-redux";
import { Autocomplete } from "@material-ui/lab";
import InventoryReconciliation from "./InventoryReconciliation";
// import ViewReconciliationHistory from "./ViewReconciliationHistory";
import usePrevious from "../../helpers/previousProps";
import CustomTableCell from "../models/table/CustomTableCell";
import { convertToLocaleDateTime } from "../functionalComponents/FormatDateTimeToLocale";
import { fetchInventoryStatusFilters } from "../../../containers/actions/common/inventoryStatusActions";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { getFeaturesAccess } from "../../../containers/actions/common/accessControl";

const useStyles = makeStyles((theme) => ({
  table: {
    /*
      BCM-881 — minWidth 650 made the browser compress as many as 20 columns
      into the viewport, so the container's horizontal scrollbar never engaged
      and the headers were crushed into three ragged lines instead. Sizing to
      max-content lets every column keep its natural width and hands the
      overflow to the scrollbar; minWidth 100% still fills the container when
      the column set is small (Show total qty. only).
    */
    width: "max-content",
    minWidth: "100%",
    borderCollapse: "collapse",
    "& .MuiTableCell-stickyHeader": {
      backgroundColor: "#FFE4C2 ",
      BorderTop: "1px solid #ffab91",
      BorderBottom: "1px solid #ffab91",
      Height: "2rem",
    },
    "& .MuiTableCell-root": {
      /*
        lineHeight 2.3 made a three-line header 131px deep against a one-line
        header's 65px, which is the misalignment UAT reported. The padding and
        type size step down on smaller screens so the same column set stays
        readable without the row rhythm collapsing.
      */
      lineHeight: "1.45",
      padding: "14px 12px",
      fontSize: "0.9rem",
      [theme.breakpoints.down("md")]: {
        padding: "10px 8px",
        fontSize: "0.8rem",
      },
      [theme.breakpoints.up("xl")]: {
        padding: "16px 16px",
      },
    },
  },
  headerCell: {
    fontWeight: 500,
  },
  grayRow: {
    backgroundColor: "#f7f6f6",
  },
  spaceBetween: {
    lineHeight: "2",
  },
  lotNumbers: {
    display: "block",
    lineHeight: "1.5",
  },
  stickyHeaderRow: {
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  buttonStyle: {
    borderRadius: "5rem",
    whiteSpace: "nowrap",
    cursor: "pointer",
    backgroundColor: "#ec6535",
    color: "#fff",
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#9E4D27",
      color: "#fff",
    },
  },
  savebutton: {
    borderRadius: "5rem",
    whiteSpace: "nowrap",
    cursor: "pointer",
    backgroundColor: "#ec6535",
    color: "#fff",
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#9E4D27",
      color: "#fff",
    },
  },
  btnSync: {
    background:
      "transparent linear-gradient(251deg, #173072 0%, #301232 100%) 0% 0% no-repeat padding-box;",
    color: "white",
    textTransform: "none",
    borderRadius: "2rem",

    padding: "5px 20px",
    "&:hover": {
      backgroundColor: "green",
      background: "green",
      color: "white",
    },
  },
  btnSave: {
    textTransform: "none",
    borderRadius: "50px",
    backgroundColor: "#ec6535",
    color: "white",
    "&:hover": {
      backgroundColor: "#ec6535",
    },
  },
  centeredCell: {
    verticalAlign: "middle",
  },
  cellStyle: {
    width: "10%",
    margin: 0,
    padding: 0,
    // outline: "1px solid pink",
  },
  collapsedRow: {
    backgroundColor: "#f7f6f6",
  },
  smallGrid: {
    minWidth: 140,
    [theme.breakpoints.up('sm')]: {
      flexBasis: '13%',
      maxWidth: '13%',
    }
  },
  materialCodeGrid: {
    minWidth: 200,
    [theme.breakpoints.up('sm')]: {
      flexBasis: '18%',
      maxWidth: '18%',
    },
  },
  // BCM-881: the two option cards that sit above the table — "Group rows" and
  // "Show <BMS|CMO> total qty. only". Each is a bordered tile carrying a bold
  // label and a lighter hint line beneath it.
  optionCard: {
    display: "flex",
    alignItems: "flex-start",
    border: "1px solid #e0e0e0",
    borderRadius: "4px",
    backgroundColor: "#fafafa",
    padding: "4px 12px 4px 4px",
  },
  optionCardDisabled: {
    opacity: 0.6,
  },
  optionLabel: {
    fontSize: "0.875rem",
    fontWeight: 500,
    lineHeight: 1.3,
  },
  optionHint: {
    fontSize: "0.75rem",
    color: "#757575",
    lineHeight: 1.3,
  },
  // BCM-881: the ⓘ beside a heading or a quantity column header.
  infoIcon: {
    fontSize: "1rem",
    color: "#9e9e9e",
    verticalAlign: "middle",
    marginLeft: "4px",
    cursor: "pointer",
  },
  // BCM-881: the "+1", "+2" … pill in the CMO Batch Numbers column, opening the
  // CMO batches mapped to one BMS batch.
  moreBatchesButton: {
    minWidth: "auto",
    padding: "0 10px",
    height: "22px",
    borderRadius: "11px",
    fontSize: "0.75rem",
    textTransform: "none",
    marginLeft: "8px",
  },
}));

/*
  BCM-881 — the four BMS SAP inventory status columns.

  The FSD fixes both the set and the order: Unrestricted Stock, Quality
  Inspection Stock, Blocked Stock, Restricted Stock, shown immediately before
  BMS total qty., which is their sum.

  `fields` is the field read off a lot row. The inventory API carries all four,
  and they add up to sponsorTotalQuantity, which BMS total qty. shows. Unlike
  the CMO status columns these are not configured anywhere: the set is fixed by
  the FSD, is the same for every CMO, and the Inventory Status tab in Sponsor
  Network Configuration has no bearing on it.
*/
const BMS_STATUS_COLUMNS = [
  { key: "unrestrictedStock", label: "Unrestricted Stock", fields: ["valuatedUnrestrictedUsedStock"] },
  { key: "qualityInspectionStock", label: "Quality Inspection Stock", fields: ["stockInQualityInspection"] },
  { key: "blockedStock", label: "Blocked Stock", fields: ["blockedStock"] },
  // "Resticted" is the Corda schema's own spelling of it.
  { key: "restrictedStock", label: "Restricted Stock", fields: ["totalStockOfAllRestictedBatches"] },
];

/*
  BCM-881 — the field each cell reads, newest name first.

  The inventory API forwards Corda's response almost verbatim, and BCM-881
  renames much of it: on-hand becomes sponsorTotalQuantity, committedQuantity
  becomes allocatedQuantity, expiryDate becomes sponsorBatchExpiryDate, and the
  CMO batches move from a flat cmoLotNumber to cmoLotDetails[]. The new names
  do not exist until that Corda build is deployed, and the old ones disappear
  when it is.

  So every cell reads a list rather than a name, and takes the first value that
  is actually present. The dashboard therefore works against the API as it is
  today and against the BCM-881 one, with no flag day in between.
*/
const FIELD = {
  /*
    Six fields carry two names. BCM-881 renames them in Corda and middleware
    forwards Corda's response verbatim, so the new name does not exist until
    that Corda build is deployed and the old one goes when it is. Reading both
    means the dashboard works either side of that deployment, and the second
    name can be dropped once it is live everywhere.

    The live names were read off FetchInventoryDataSponsorLatestOptimisedV1,
    the flow /fetchInventoryDataSponsor actually runs.
  */
  bmsTotalQty: ["sponsorTotalQuantity", "onHandAvailableQuantity"],
  allocatedQty: ["allocatedQuantity", "committedQuantity"],
  expiryDate: ["sponsorBatchExpiryDate", "expiryDate"],
  batchExpired: ["sponsorBatchExpired", "batchExpired"],
  batchNearExpiry: ["sponsorBatchNearExpiry", "batchNearExpiry"],
  cmoExpiryDate: ["cmoBatchExpiryDate", "cmoInventoryExpiryDate"],

  // Same name before and after BCM-881.
  netQtyAvailable: ["netAvailableQuantity"],
  uom: ["inventoryUOM"],
  manufacturingDate: ["inventoryManufacturingDate"],
  cmoTotalQty: ["cmoOnHandAvailableQuantity"],
  cmoBatchNumber: ["cmoLotNumber"],
  cmoBatchExpired: ["cmoBatchExpired"],
  cmoBatchNearExpiry: ["cmoBatchNearExpiry"],

  // New in BCM-881 — the FSD's "CMO ID … the SAP vendor ID of the CMO the
  // stock is held against". Blank until that Corda build is deployed.
  cmoID: ["vendorNumber"],
};

// First field that carries a value. 0 and false are values; "" and null are not.
const readField = (source, fields) => {
  if (!source || !Array.isArray(fields)) return undefined;
  for (const field of fields) {
    const value = source[field];
    if (value !== null && value !== undefined && value !== "") return value;
  }
  return undefined;
};

/*
  BCM-881 — BMS total qty. is the sum of Unrestricted Stock, Quality Inspection
  Stock, Blocked Stock and Restricted Stock, so show the sum of the values those
  four columns actually display.

  Reading the backend's own total instead let the two disagree on screen. These
  quantities are held as 32-bit floats, so a batch recorded as 9999.984 arrives
  as 9999.984375: the status column and the backend total then round apart and
  the row stops adding up. Adding up what is displayed cannot disagree with what
  is displayed.

  Each column is rounded here exactly as the cell rounds it, so the existing
  rounding rule is unchanged -- only where the total comes from. A CMO login
  never had these columns, so it keeps reading the backend total.

  The adding is done in whole thousandths and formatted once. Rounding each
  column to a fraction and then adding those fractions puts the error straight
  back: 0.100 + 0.200 is 0.30000000000000004, which rounds up to 0.301 against
  columns reading 0.100 and 0.200.
*/
/*
  BCM-881 — a CMO batch the CMO reports with no BMS batch mapped to it. The FSD
  has it three times, once as a business rule: "A CMO batch with no matching BMS
  batch is displayed in the same way as any other CMO batch, with the BMS columns
  on that row left blank."

  formatQty prints 0 for an absent value, so without this the three BMS quantity
  columns read 0 on those rows -- which says BMS holds none of this, where the
  truth is that there is no BMS batch here to hold any.
*/
const hasNoBmsBatch = (lot) =>
  !lot || lot.lotNumber === null || lot.lotNumber === undefined || lot.lotNumber === "";

const bmsTotalFromColumns = (lot, { blankWhenNoBmsBatch = false } = {}) => {
  // Only the CMO dashboard asks for this, and only for a row with no BMS batch;
  // everywhere else the default keeps the existing reading.
  if (blankWhenNoBmsBatch && hasNoBmsBatch(lot)) return "";
  /*
    Adding the four only works when the four are there. The CMO Inventory
    Status dashboard sends sponsorTotalQuantity and, until recently, none of
    the statuses behind it, so every absent field counted as 0 and the total
    read 0 on rows that plainly held stock. An older Corda build does the same
    on the BMS dashboard.

    So: add the columns when there are columns to add, and otherwise fall back
    to the total the backend sent. When the columns are present the sum still
    wins, because what is displayed has to add up to what is displayed.
  */
  /*
    Carries the key at all, whatever its value. A field the response sends as
    null or "" is a real zero and still belongs in the sum -- a blank column
    beside a total of 99 is exactly the disagreement this function exists to
    prevent. Only a response that omits all four has no columns to add.
  */
  const carriesAnyColumn = BMS_STATUS_COLUMNS.some((col) =>
    col.fields.some((field) => lot && Object.prototype.hasOwnProperty.call(lot, field))
  );
  if (!carriesAnyColumn) {
    const sent = readField(lot, FIELD.bmsTotalQty);
    return sent === undefined ? "0" : formatQty(sent);
  }
  const thousandths = BMS_STATUS_COLUMNS.reduce((sum, col) => {
    const value = Number(readField(lot, col.fields));
    return sum + (Number.isFinite(value) ? Math.ceil(value * 1000) : 0);
  }, 0);
  return thousandths === 0 ? "0" : (thousandths / 1000).toFixed(3);
};

/*
  The CMO batches held against a BMS batch. BCM-881 returns them as
  cmoLotDetails[]; the earlier shape carried a single cmoLotNumber on the lot
  itself, which cmoBatchesOf() below still falls back to.
*/
const cmoBatchListOf = (lot) => {
  const list = lot?.cmoLotDetails;
  return Array.isArray(list) ? list : [];
};

/*
  BCM-881 — batches reported with a total qty. of zero are not shown.

  Each view excludes on its own total, and only on its own total.

  BMS view — "Batches whose BMS total qty. is zero are not shown. The exclusion
  is applied on BMS total qty. only", so a batch carrying quantity in any
  status stays, however its Net Qty. Available reads.

  CMO view — "CMO batches whose CMO total qty. is zero are not shown,
  irrespective of the BMS total qty. held against the BMS batch they are mapped
  to … The exclusion is applied on CMO total qty. only. A BMS batch carrying a
  BMS total qty. of zero is displayed where a CMO batch mapped to it carries
  quantity." The BMS row goes only once every CMO batch under it has gone. The
  dashboard is a view of CMO inventory, so a zero on the BMS side says nothing
  about whether the CMO still holds that batch.

  A field the API did not send is not a reported zero: readField returns
  undefined for an absent value, and only a value that really is 0 excludes
  anything. Without that, a build that has not shipped the field yet would
  empty the dashboard.
*/
const isReportedZero = (value) =>
  value !== undefined && value !== null && value !== "" && Number(value) === 0;

/*
  The lot to display, or null to drop it. Returns the lot untouched unless a
  CMO batch had to be removed, in which case it is copied with the survivors.
*/
const lotAfterZeroExclusion = (lot, isCmoView) => {
  if (!isCmoView) {
    return isReportedZero(readField(lot, FIELD.bmsTotalQty)) ? null : lot;
  }

  const batches = cmoBatchListOf(lot);
  if (batches.length === 0) {
    // The shape before BCM-881 carries the CMO quantity on the lot itself.
    return isReportedZero(readField(lot, FIELD.cmoTotalQty)) ? null : lot;
  }
  const kept = batches.filter(
    (batch) => !isReportedZero(readField(batch, FIELD.cmoTotalQty))
  );
  if (kept.length === 0) return null;
  if (kept.length === batches.length) return lot;

  /*
    The row shows its first CMO batch from the lot itself and the rest behind
    the "+N" control, so a lot whose own CMO batch was the excluded one has to
    take a survivor's place — otherwise the batch just removed is the one on
    display. Only the cmo* fields move across, by name, so a field added to
    the batch later comes along without this needing to know about it.
  */
  const primary = readField(lot, FIELD.cmoBatchNumber);
  const primarySurvives = kept.some(
    (batch) => readField(batch, FIELD.cmoBatchNumber) === primary
  );
  const promoted = primarySurvives
    ? {}
    : Object.fromEntries(
      Object.entries(kept[0] || {}).filter(([key]) =>
        key.toLowerCase().startsWith("cmo")
      )
    );
  return { ...lot, ...promoted, cmoLotDetails: kept };
};

/*
  Drops the excluded batches, and any material left with none.

  Applied here rather than by the API for now. Pagination is server side and
  counts material groups, so numberOfPages and totalNoOfRecords still describe
  what the API returned — a page can therefore show fewer rows than the page
  size once the excluded batches are gone.
*/
const withoutZeroQtyBatches = (list, isCmoView) =>
  (Array.isArray(list) ? list : [])
    .map((material) => ({
      ...material,
      lotNumbersList: (material?.lotNumbersList || [])
        .map((lot) => lotAfterZeroExclusion(lot, isCmoView))
        .filter(Boolean),
    }))
    .filter((material) => material.lotNumbersList.length > 0);

/*
  A CMO status quantity, from quantityBasedOnStatus[] on a CMO batch.

  Called with a CMO batch it reads that batch. Called with a BMS lot it sums
  across the CMO batches mapped to it, which is the FSD's "The BMS batch row
  shows the sum of the CMO batches mapped to it."
*/
// The names a status column answers to, compared without case (the FSD makes
// status matching case-insensitive): a configured column is known by its
// Renamed Status/Column name and by the status or column name in the CMO's file,
// since Corda may echo either onto quantityBasedOnStatus.
const namesOf = (column) =>
  (typeof column === "string" ? [column] : column?.aliases?.length ? column.aliases : [column?.label])
    .filter(Boolean)
    .map((name) => String(name).trim().toLowerCase());

const cmoStatusQty = (source, column) => {
  if (!source) return undefined;
  if (Array.isArray(source.quantityBasedOnStatus)) {
    const names = namesOf(column);
    const entry = source.quantityBasedOnStatus.find((row) =>
      [row?.renamedColumnName, row?.statusName]
        .filter(Boolean)
        .some((name) => names.includes(String(name).trim().toLowerCase()))
    );
    return entry ? entry.quantity : undefined;
  }
  const values = cmoBatchListOf(source)
    .map((batch) => cmoStatusQty(batch, column))
    .filter((value) => value !== undefined && value !== null);
  if (!values.length) return undefined;
  return values.reduce((sum, value) => sum + (Number(value) || 0), 0);
};

/*
  CMO Unrestricted Qty. -- the portion of CMO total qty. the CMO reports as
  unrestricted (FSD).

  Corda computes it and sends it as cmoNetAvailableQty: on the BMS batch it is
  summed across the CMO batches mapped to it and converted to the BMS UoM, like
  cmoOnHandAvailableQuantity beside it; on each CMO batch it is that batch's own
  figure. That value is used where present. Otherwise it is summed from the
  quantityBasedOnStatus rows flagged partOfCMOUnrestrictedQty. Neither -- Patheon
  until its inventory IDocs arrive -- means NA.
*/
const cmoUnrestrictedQty = (source) => {
  if (!source) return undefined;
  const fromCorda = source.cmoNetAvailableQty;
  if (fromCorda !== undefined && fromCorda !== null && fromCorda !== "" && Number.isFinite(Number(fromCorda))) {
    return Number(fromCorda);
  }
  if (Array.isArray(source.quantityBasedOnStatus)) {
    const rows = source.quantityBasedOnStatus.filter(
      (row) => row?.partOfCMOUnrestrictedQty
    );
    if (!rows.length) return undefined;
    return rows.reduce((sum, row) => sum + (Number(row.quantity) || 0), 0);
  }
  const values = cmoBatchListOf(source)
    .map(cmoUnrestrictedQty)
    .filter((value) => value !== undefined && value !== null);
  if (!values.length) return undefined;
  return values.reduce((sum, value) => sum + (Number(value) || 0), 0);
};

/*
  BCM-881 — the hard coded tooltip texts, quoted from the FSD.

  The two CMO ones are placeholders: the FSD has the system generate them from
  the CMO Inventory Mapping held against the CMO in CMO Level Configurations,
  so once that configuration is delivered these fall back to whatever the API
  returns for the selected CMO (see cmoQuantityTooltips below).
*/
/*
  The Material Code / Description column. It carries the longest text in the
  table, so it gets a floor of 260px and wraps at word breaks onto two or
  three lines. The table cell's default is nowrap with an ellipsis, and the
  material row used to set maxWidth 0, so the browser shrank this column
  until each word was clipped ("ENTECAVI...") and the cell ran to 7 lines,
  while CMO, Brand and Material Type kept their full width.
*/
const MATERIAL_CELL_STYLE = {
  minWidth: 260,
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  textOverflow: "clip",
};

const TOOLTIP_TEXT = {
  bmsTotalQty:
    "BMS total qty. column values are sum of - Unrestricted Stock, Quality Inspection Stock, Blocked Stock and Restricted Stock quantities.",
  netQtyAvailable:
    "Net Qty. Available is the Unrestricted Stock quantity minus the Allocated Qty. for that batch.",
  bmsNetQtyAvailable:
    "BMS Net Qty. Available is the Unrestricted Stock quantity minus the Allocated Qty. for that batch.",
  allocatedQty:
    "Allocated Qty. is the quantity from this batch already reserved against confirmed CMO orders.",
  bmsAllocatedQty:
    "BMS Allocated Qty. is the quantity from this batch already reserved against confirmed CMO orders.",
  cmoTotalQty:
    "CMO total qty. column values are the sum of all inventory status quantities reported by the CMO.",
  cmoUnrestrictedQty:
    "CMO Unrestricted Qty. is the portion of CMO total qty. that is unrestricted and available for use, as reported by the CMO.",
  uomConversion:
    "Note - If CMO UoM is not the same as BMS UoM, Mosymphony converts the quantities to match BMS UoM and displays these values in the Inventory dashboard.",
};

/*
  The CMO total qty. / CMO Unrestricted Qty. tooltip for the CMO in view, written
  from the quantityBasedOnStatus[] rows Corda returns on each CMO batch: the
  statuses flagged into that quantity (partOfCMOTotalQty /
  partOfCMOUnrestrictedQty), e.g. "CMO total qty. column values are the same as
  Closing Stock." Null when no row is flagged, so the FSD's fixed text is used.

  Each status is named by its Renamed Status/Column, the same name the column
  header carries, so the tooltip names columns the reader can actually find.
  It used to prefer the CMO's own file name: for a CMO whose file names its
  statuses differently -- Vetter's German headings against an English mapping --
  the tooltip then listed three names that appeared nowhere on the dashboard.
*/
const joinNames = (names) =>
  names.length === 1 ? names[0] : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

const cmoQuantityTooltip = (statusRows, flag) => {
  const names = [];
  (Array.isArray(statusRows) ? statusRows : []).forEach((row) => {
    const name = row && row[flag] ? row.renamedColumnName || row.statusName : null;
    if (name && !names.includes(name)) names.push(name);
  });
  if (!names.length) return null;
  if (flag === "partOfCMOTotalQty") {
    return names.length === 1
      ? `CMO total qty. column values are the same as ${names[0]}.`
      : `CMO total qty. column values are the sum of ${joinNames(names)} quantities.`;
  }
  return names.length === 1
    ? `CMO Unrestricted Qty. is the same as ${names[0]}.`
    : `CMO Unrestricted Qty. is the sum of ${joinNames(names)} quantities.`;
};

/*
  BCM-881 — the ⓘ shown beside a heading or a column header. The span wrapper is
  what MUI v4 needs to hang a tooltip off an SVG icon reliably.
*/
/*
  BCM-881 — the look of these tooltips.

  MUI v4 defaults a tooltip to 10px type in a box capped at 300px, which turns
  the FSD's sentences into several lines of very small print. These carry real
  explanatory text, so they are set at normal reading size in a wider box with
  room to breathe.

  Shared by the ⓘ tooltips and the one on the disabled "Show CMO total qty.
  only" tile, so the two cannot drift apart.
*/
const useTooltipStyles = makeStyles((theme) => ({
  /*
    The "&&" doubles the generated class in the selector, which is what makes
    these beat MuiTooltip-tooltip's own rule. Both are single classes
    otherwise, so without it the winner is decided by whichever style sheet
    JSS happens to inject last and the defaults can come back.
  */
  tooltip: {
    "&&": {
      fontSize: "0.875rem",
      fontWeight: 400,
      lineHeight: 1.55,
      letterSpacing: "0.01em",
      maxWidth: 460,
      padding: "10px 14px",
      borderRadius: 6,
      backgroundColor: "#37474f",
      boxShadow: theme.shadows[4],
    },
  },
  arrow: {
    "&&": {
      color: "#37474f",
    },
  },
}));

const InfoTip = ({ title, className }) => {
  const tooltipClasses = useTooltipStyles();
  if (!title) return null;
  return (
    <Tooltip title={title} arrow placement="top" classes={tooltipClasses}>
      <span style={{ display: "inline-flex", alignItems: "center" }}>
        <InfoIcon className={className} />
      </span>
    </Tooltip>
  );
};

/*
  BCM-881 — quantity formatting, lifted out of the cells so that the status
  columns, the totals and the existing columns all round identically.

  The rule is the one already used across this table: round up at the third
  decimal and always show three decimals. A quantity that is absent -- a batch
  that does not report that status at all -- renders blank rather than "0", so
  that a missing value is not read as a reported zero.
*/
const formatQty = (value, { blankWhenMissing = false } = {}) => {
  if (value === null || value === undefined || value === "") {
    return blankWhenMissing ? "" : "0";
  }
  const num = Number(value);
  if (!Number.isFinite(num)) return blankWhenMissing ? "" : "0";
  if (num === 0) return "0";
  return (Math.ceil(num * 1000) / 1000).toFixed(3);
};



/*
  BCM-881 — the grid takes whatever height is left beneath it, measured rather
  than assumed. A fixed offset cannot hold: the filter row wraps to two or three
  lines as the window narrows, and the browser chrome differs between machines,
  so the space above the table is only known at runtime. Measuring keeps the
  page itself from needing a scrollbar on any screen size.
*/
const MIN_GRID_HEIGHT = 260;
const GRID_BOTTOM_GAP = 96; // the pagination row and the margin above it

const useAvailableHeight = (ref, deps) => {
  const [height, setHeight] = useState(null);
  React.useLayoutEffect(() => {
    const measure = () => {
      const node = ref.current;
      if (!node) return;
      const top = node.getBoundingClientRect().top;
      const available = window.innerHeight - top - GRID_BOTTOM_GAP;
      setHeight(Math.max(Math.round(available), MIN_GRID_HEIGHT));
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("orientationchange", measure);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("orientationchange", measure);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return height;
};

const InventoryStatusDashboard = () => {
  const classes = useStyles();
  // Same tooltip look as the ⓘ ones, for the hint on the disabled tile below.
  const tooltipClasses = useTooltipStyles();
  const dispatch = useDispatch();
  const [showTable, setShowTable] = useState(false);
  const [openRows, setOpenRows] = useState({});
  const [openDialog, setOpenDialog] = useState(false);
  const [reconDialog, setReconDialog] = useState(false);
  const [vendorData, setVendorData] = useState({});
  const [selectedCMO, setSelectedCMO] = useState({ organizationId: 'All', organizationName: "All" });
  // The CMO the rows on screen were last fetched for (set on every fetch).
  const [appliedCmoId, setAppliedCmoId] = useState("All");
  const [selectedPlant, setSelectedPlant] = useState({ plantCode: 'All' });
  const [filteredMaterialCodes, setFilteredMaterialCodes] = useState([]);
  const [selectedMaterialCode, setSelectedMaterialCode] = useState({ materialCode: 'All', materialDescription: 'All' });
  const [selectedBatchNumber, setSelectedBatchNumber] = useState({ inventoryBatchNumber: 'All' });
  const [selectedStorageLocation, setSelectedStorageLocation] = useState({ storageLocationID: 'All' })
  const [filteredBatchNumbers, setFilteredBatchNumbers] = useState([]);
  const [inventoryList, setInventoryList] = useState([]);
  // const [filterLoading, setFilterLoading] = useState(false);
  // const [filter, setFilter] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20); // Default rows per page
  const [paginatedData, setPaginatedData] = useState([]);
  const [savedFilters, setSavedFilters] = useState({
    vendorPlant: "all",
    vendorName: "all",
    rmpmCodeDescription: "all",
    sponsorLotNumber: "all",
  });
  const [inventoryToggleValue, setInventoryToggleValue] = useState(localStorage.collabType === "Pharma Co" ? "BMS" : null)
  const filtersFetchedRef = useRef(false);
  const [inventorySyncTime, setInventorySyncTime] = useState(null);

  /*
    BCM-881 — the two options above the table.

    Group rows is selected by default, so the dashboard opens in the grouped
    view it has always opened in. Show total qty. only is cleared by default,
    so the status columns are visible until the user hides them. Neither
    changes a value; both only change what is on screen.
  */
  const [groupRows, setGroupRows] = useState(true);
  const [showTotalQtyOnly, setShowTotalQtyOnly] = useState(false);

  /*
    BCM-881 — which CMO batch rows are open under a BMS batch, keyed
    "<rowKey>", for the "+1" / "+2" control in the CMO Batch Numbers column.
    Separate from openRows, which drives the material level chevron.
  */
  const [openCmoBatches, setOpenCmoBatches] = useState({});

  const isCMOSelected = localStorage.collabType !== "CMO" && !!selectedCMO?.organizationId && selectedCMO?.organizationId !== 'All';
  const isStorageLocationSelected = !!selectedStorageLocation?.storageLocationID && selectedStorageLocation?.storageLocationID !== 'All';

  const getOrganizationIdFromToken = () => {
    const authToken = localStorage.getItem("authToken");
    if (!authToken) return null;

    try {
      const decodedToken = jwtDecode(authToken);
      return decodedToken.organization_id || null;
    } catch (error) {
      return null;
    }
  };

  const screenAccess = useSelector(
    (state) =>
      state.pcmp.common.accessControl.screensAccess?.data
        .inventoryReconciliationDashboard
  );

  const featuresAccess = useSelector(
    (state) =>
      state.pcmp.common.accessControl.featuresAccess
        ?.inventoryReconciliationDashboardFeature
  );

  useEffect(() => {
    if (screenAccess?.screenAccess && !featuresAccess) {
      dispatch(
        getFeaturesAccess(
          screenAccess?.screenId,
          "inventoryReconciliationDashboardFeature"
        )
      );
    }
  }, [dispatch, screenAccess, featuresAccess]);

  const inventoryData = useSelector((state) => {
    return state?.pcmp?.common?.inventoryStatus?.inventoryStatusDetails;
  });

  const prevInventoryData = usePrevious(inventoryData);

  const inventoryDataLoading = useSelector((state) => {
    return state?.pcmp?.common?.inventoryStatus?.inventoryStatusDetailsLoading;
  });

  const cmosList = useSelector((state) => {
    return state?.pcmp?.common?.inventoryStatus?.cmosDetails;
  });

  const plantCodesList = useSelector((state) => {
    return state?.pcmp?.common?.inventoryStatus?.plantCodeDetails;
  });

  const storageLocationList = useSelector((state) => {
    return state?.pcmp?.common?.inventoryStatus?.storageLocationDetails;
  });

  const materialCodesList = useSelector((state) => {
    return state?.pcmp?.common?.inventoryStatus?.materialCodesDetails;
  });

  const lotNumbersList = useSelector((state) => {
    return state?.pcmp?.common?.inventoryStatus?.lotNumberDetails;
  });

  // const inventoryStatusSaveList = useSelector((state) => {
  //   return state?.pcmp?.common?.inventoryStatus?.inventoryStatusSaveDetails;
  // });

  useEffect(() => {
    if (JSON.stringify(inventoryData) !== JSON.stringify(prevInventoryData)) {
      setInventoryList(inventoryData);
    }

  }, [inventoryData, prevInventoryData]);

  useEffect(() => {
    if (inventoryData && inventoryData?.sponsorLatestInventorySyncTime && inventoryToggleValue === "BMS") {
      setInventorySyncTime(inventoryData?.sponsorLatestInventorySyncTime)
    } else if (inventoryData?.cmoLatestInventorySyncTime && inventoryToggleValue === "CMO") {
      setInventorySyncTime(inventoryData?.cmoLatestInventorySyncTime)
    } else if (inventoryData?.cmoLatestInventorySyncTime && localStorage.collabType === 'CMO' && inventoryToggleValue === null) {
      setInventorySyncTime(inventoryData?.cmoLatestInventorySyncTime)
    } else {
      setInventorySyncTime(null)
    }

  }, [inventoryData, inventoryToggleValue])

  let showPagination = inventoryList?.totalNoOfRecords > 0;

  useEffect(() => {
    const list = inventoryList?.inventoryDataList;
    if (!list?.length) {
      setPaginatedData(list);
      return;
    }
    // Gated with everything else BCM-881 adds, so a CMO login is untouched.
    const visible =
      localStorage.collabType !== "CMO"
        ? withoutZeroQtyBatches(list, inventoryToggleValue === "CMO")
        : list;
    const sorted = [...visible].sort((a, b) =>
      (a?.vendorName ?? "").localeCompare(b?.vendorName ?? "", undefined, { sensitivity: "base" })
    );
    setPaginatedData(sorted);
  }, [inventoryList, currentPage, inventoryToggleValue]);

  const totalPages = inventoryList?.numberOfPages;

  const getDecodedAuthToken = () => {
    const authToken = localStorage.getItem("authToken");
    if (!authToken) return null;

    try {
      const decodedToken = jwtDecode(authToken);
      return decodedToken;
    } catch (error) {
      console.error("Error decoding JWT token:", error);
      return null;
    }
  };

  const decodedTokenData = getDecodedAuthToken();
  const organizationIdFromToken = decodedTokenData?.organization_id || null;

  useEffect(() => {
    if (localStorage.collabType !== "CMO") {
      dispatch(fetchCMOMembers());
    }
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchAllReceivingPlantIDs());
    dispatch(fetchAllRMPMCodes());
    dispatch(fetchAllLotnumbers());
    dispatch(fetchAllStorageLocationIds())
  }, [dispatch]);




  useEffect(() => {

    const fetchFilters = async () => {
      // setFilterLoading(true);
      try {

        // Use the unified API to fetch filters
        const result = await dispatch(fetchInventoryStatusFilters());

        if (result && result.status && result.save_filter_options) {
          const inventoryFilters = result.save_filter_options;
          // setFilter(inventoryFilters);
          setSavedFilters(inventoryFilters);
          // Set filter UI state from fetched filters
          if (inventoryFilters && Object.keys(inventoryFilters)?.length > 0) {
            // Find complete CMO object from the list
            if (inventoryFilters.vendorName !== "all" && cmosList?.CMOMembersWithId) {
              const matchedCMO = cmosList.CMOMembersWithId.find(
                (cmo) => cmo.organizationName === inventoryFilters.vendorName
              );
              setSelectedCMO(matchedCMO || { organizationId: "All", organizationName: "All" });
            } else {
              setSelectedCMO({ organizationId: "All", organizationName: "All" });
            }

            // Find complete Plant object from the list
            if (inventoryFilters.vendorPlant !== "all" && plantCodesList?.data) {
              const matchedPlant = plantCodesList.data.find(
                (plant) => plant.plantCode === inventoryFilters.vendorPlant
              );
              setSelectedPlant(matchedPlant || { plantCode: "All" });
            } else {
              setSelectedPlant({ plantCode: "All" });
            }

            // Find complete Material Code object from the list
            if (inventoryFilters.rmpmCodeDescription !== "all" && materialCodesList?.data) {
              const matchedMaterialCode = materialCodesList.data.find(
                (material) => material.materialCode === inventoryFilters.rmpmCodeDescription
              );
              setSelectedMaterialCode(matchedMaterialCode || { materialCode: "All", materialDescription: "All" });
            } else {
              setSelectedMaterialCode({ materialCode: "All", materialDescription: "All" });
            }

            // Find complete Batch Number object from the list
            if (inventoryFilters.sponsorLotNumber !== "all" && lotNumbersList?.data) {
              const matchedBatchNumber = lotNumbersList.data.find(
                (lot) => lot.inventoryBatchNumber === inventoryFilters.sponsorLotNumber
              );
              setSelectedBatchNumber(matchedBatchNumber || { inventoryBatchNumber: "All" });
            } else {
              setSelectedBatchNumber({ inventoryBatchNumber: "All" });
            }
          } else {
            // setFilter({});
            setSelectedCMO({ organizationId: "All", organizationName: "All" });
            setSelectedPlant({ plantCode: "All" });
            setSelectedMaterialCode({ materialCode: "All", materialDescription: "All" });
            setSelectedBatchNumber({ inventoryBatchNumber: "All" });
          }
        }
      } catch (err) {
        console.error("Error fetching inventory status filters:", err);
      } finally {
        // setFilterLoading(false);
      }
    };

    // Fetch filters only after all dropdown lists are loaded and only once
    // For CMO users, cmosList is not needed, so we skip that check
    const listsLoaded = localStorage.collabType === "CMO"
      ? plantCodesList && materialCodesList && lotNumbersList
      : cmosList && plantCodesList && materialCodesList && lotNumbersList;

    if (!filtersFetchedRef.current && listsLoaded) {
      filtersFetchedRef.current = true;
      fetchFilters();
    }
  }, [dispatch, cmosList, plantCodesList, materialCodesList, lotNumbersList]);

  const handleSaveFilters = async () => {
    const filtersToSave = {
      vendorPlant: (!selectedPlant?.plantCode || selectedPlant?.plantCode === "All") ? "all" : selectedPlant?.plantCode,
      vendorName: (!selectedCMO?.organizationName || selectedCMO?.organizationName === "All") ? "all" : selectedCMO?.organizationName,
      rmpmCodeDescription: (!selectedMaterialCode?.materialCode || selectedMaterialCode?.materialCode === "All") ? "all" : selectedMaterialCode?.materialCode,
      rmpmDescription: (!selectedMaterialCode?.materialDescription || selectedMaterialCode?.materialDescription === "All") ? "all" : selectedMaterialCode?.materialDescription,
      sponsorLotNumber:
        (!selectedBatchNumber?.inventoryBatchNumber || selectedBatchNumber?.inventoryBatchNumber === "All") ? "all" : selectedBatchNumber?.inventoryBatchNumber,
      storageLocationID: (!selectedStorageLocation.storageLocationID || selectedStorageLocation.storageLocationID === "All") ? "all" : selectedStorageLocation.storageLocationID,
    };

    if (JSON.stringify(filtersToSave) !== JSON.stringify(savedFilters)) {
      setSavedFilters(filtersToSave);
      try {
        // Save filters using the unified API - only save, don't fetch data
        await dispatch(saveInventoryStatusFilters(filtersToSave));
      } catch (error) {
        console.error("Error saving filters:", error);
      }
    }
  };

  /*
    BCM-881 — one place that builds an inventory request, and one that sends it.

    The two dashboards are now served by different endpoints: BMS Inventory
    Status by fetchInventoryData, CMO Inventory Status by fetchInventoryDataCMO.
    So "View CMO Inventory" has to fetch, which it never had to before, when a
    single response carried both views.

    The storage location belongs to the BMS dashboard alone -- cmo_inventory has
    no storage location, and the CMO controller ignores the field. The CMO
    request therefore leaves out storageLocationID AND the vendorMosymphonyID of
    0 that always travels with it: those two are one filter ("plant-held stock,
    which carries vendor 0"), and sending half of it would ask for every
    plant-held row rather than the ones in that location.

    The four requests were four copies of the same object, which is how the two
    halves of that filter drifted apart in the first place; they are one here.

    A CMO login never reaches the CMO view, so it keeps sending exactly what it
    sends today, to the endpoint it uses today.
  */
  const inventoryEndpointFor = (cmoView) =>
    cmoView ? "fetchInventoryDataCMO" : "fetchInventoryData";

  const buildInventoryRequest = ({ pageNumber, pageSize, cmoView }) => {
    const isCmoLogin = localStorage.collabType === "CMO";
    const selectedVendor =
      (!selectedCMO?.organizationId || selectedCMO?.organizationId === "All")
        ? "all"
        : selectedCMO?.organizationId;

    const request = {
      [isCmoLogin ? "brandOwnerMosymphonyID" : "vendorMosymphonyID"]: isCmoLogin
        ? inventoryList?.inventoryDataList?.[0]?.brandOwnerMosymphonyID || "all"
        : cmoView
          ? selectedVendor
          : isStorageLocationSelected ? 0 : selectedVendor,
      vendorPlant: (!selectedPlant?.plantCode || selectedPlant?.plantCode === "All") ? "all" : selectedPlant?.plantCode,
      materialCode: (!selectedMaterialCode?.materialCode || selectedMaterialCode?.materialCode === "All") ? "all" : selectedMaterialCode?.materialCode,
      inventoryBatchNumber:
        (!selectedBatchNumber?.inventoryBatchNumber || selectedBatchNumber?.inventoryBatchNumber === "All") ? "all" : selectedBatchNumber?.inventoryBatchNumber,
      pageNumber,
      pageSize,
    };

    if (!cmoView) {
      request.storageLocationID = isCMOSelected
        ? null
        : (!selectedStorageLocation.storageLocationID || selectedStorageLocation.storageLocationID === "All")
          ? "all"
          : selectedStorageLocation.storageLocationID;
    }

    // A CMO login sends its own organisation alongside the brand owner, as it
    // does today.
    if (isCmoLogin) {
      request.vendorMosymphonyID = getOrganizationIdFromToken() || "all";
    }

    return request;
  };

  const loadInventory = ({
    pageNumber,
    pageSize,
    // The toggle passes this explicitly, because it fires before the state it
    // has just set has been applied.
    cmoView = inventoryToggleValue === "CMO",
  }) => {
    dispatch({
      type: FETCH_ALL_INVENTORY_DATA_LOADING,
      payload: true,
    });
    setAppliedCmoId(selectedCMO?.organizationId || "All");
    dispatch(
      fetchAllInventoryData(
        buildInventoryRequest({ pageNumber, pageSize, cmoView }),
        inventoryEndpointFor(cmoView)
      )
    );
  };

  const handleRefresh = () => {
    loadInventory({ pageNumber: currentPage, pageSize: rowsPerPage });
  };

  const handleClearFilters = async () => {
    setCurrentPage(1);
    setShowTable(false);
    setRowsPerPage(20);
    setSelectedMaterialCode({ materialCode: "All", materialDescription: "All" });
    setSelectedBatchNumber({ inventoryBatchNumber: "All" });
    setSelectedCMO({ organizationId: "All", organizationName: "All" });
    setSelectedPlant({ plantCode: "All" });
    setSelectedStorageLocation({ storageLocationID: "All" })
    setInventoryToggleValue(localStorage.collabType === "Pharma Co" ? "BMS" : null)
    // setFilteredMaterialCodes(materialCodesList?.data);
    // setFilteredBatchNumbers(lotNumbersList?.data );

    // Clear filters on the server by setting all to "all"
    // try {
    //   const clearedFilters = {
    //     vendorPlant: "all",
    //     vendorName: "all",
    //     rmpmCodeDescription: "all",
    //     rmpmDescription: "all",
    //     sponsorLotNumber: "all",
    //   };

    //   await dispatch(saveInventoryStatusFilters(clearedFilters));
    //   setSavedFilters(clearedFilters);
    // } catch (error) {
    //   console.error("Error clearing filters:", error);
    // }
  };

  const handleViewReport = () => {
    setShowTable(true);
    setCurrentPage(1);
    loadInventory({ pageNumber: 1, pageSize: rowsPerPage });
  };

  /* ---------------------------------------------------------------------
     BCM-881 — Filter Hierarchy (FSD)

     "Selecting a value in any filter narrows the values offered in the other
     filters to those valid for that selection."

     Two things were wrong before. Each filter defaults to an object rather
     than null -- { plantCode: 'All' } -- so `!selectedPlant` was never true
     and the test became `item.vendorPlant === 'All'`, asking for a plant
     literally named All. No row carries that, so picking a CMO emptied the
     Material Code list entirely, and picking a plant on its own emptied it
     too. The Batch Number list had the same three tests, hidden behind an
     `if (filteredData.length > 0)` that left the previous list on screen
     instead of an empty one. And only Material and Batch were narrowed at
     all: CMO was never narrowed, and Plant only ever by CMO.

     'All' now means "do not narrow on this one", which is how the guard that
     sits above these already behaved, and every list is narrowed by every
     other selection it can see.

     The lists are built from the inventory itself -- fetchAllRMPMCodes and
     fetchAllLotnumbers both SELECT DISTINCT ... FROM inventory -- so a value
     with no stock behind it is never offered, as the FSD requires.

     A field a row does not carry cannot disagree with a selection, so it is
     skipped rather than treated as a mismatch. That is what lets a storage
     location narrow the material and batch lists on the BMS side, where both
     endpoints now send storageLocationID, and leave them alone on the CMO side,
     where cmo_inventory has no storage location to send.
     --------------------------------------------------------------------- */
  const ALL_FILTER = "All";
  const chosenValue = (value) =>
    value === undefined || value === null || value === "" || value === ALL_FILTER
      ? null
      : value;

  const activeFilters = {
    // A CMO login only ever sees its own organisation.
    cmo:
      localStorage.collabType === "CMO"
        ? organizationIdFromToken
        : chosenValue(selectedCMO?.organizationId),
    plant: chosenValue(selectedPlant?.plantCode),
    storageLocation: chosenValue(selectedStorageLocation?.storageLocationID),
    material: chosenValue(selectedMaterialCode?.materialCode),
    batch: chosenValue(selectedBatchNumber?.inventoryBatchNumber),
  };

  /*
    A row belongs in a filter's list when every other filter agrees with it.
    `except` is the filter being populated -- a list is never narrowed by its
    own selection. A field the row does not carry cannot disagree, so it is
    skipped rather than treated as a mismatch.
  */
  const rowMatchesFilters = (row, except) => {
    if (!row) return false;
    const f = activeFilters;
    if (except !== "cmo" && f.cmo !== null && row.vendorMosymphonyID !== f.cmo) return false;
    if (except !== "plant" && f.plant !== null && row.vendorPlant !== f.plant) return false;
    if (
      except !== "storageLocation" && f.storageLocation !== null &&
      row.storageLocationID !== undefined && row.storageLocationID !== f.storageLocation
    ) return false;
    if (
      except !== "material" && f.material !== null &&
      row.materialCode !== undefined && row.materialCode !== f.material
    ) return false;
    if (
      except !== "batch" && f.batch !== null &&
      row.inventoryBatchNumber !== undefined && row.inventoryBatchNumber !== f.batch
    ) return false;
    return true;
  };

  // One entry per distinct value, so narrowing cannot surface the same code twice.
  const distinctBy = (rows, key) =>
    Array.from(new Map((rows || []).map((row) => [row[key], row])).values());

  /*
    BCM-881 — choosing one filter must not throw the others away.

    Every selection used to clear every other: picking a CMO reset Plant,
    Storage Location, Material and Batch, and picking a Storage Location reset
    those plus the CMO. The FSD has the opposite -- "Plant ID, BMS Material Code
    and BMS Batch Number stay enabled and are populated" -- and wiping them
    defeats "The user can filter in any order", since picking a material and
    then a CMO lost the material.

    A selection the new choice has made impossible cannot stay either, or the
    report would ask for a combination no batch satisfies. So a selection is
    kept while the inventory holds a batch carrying both it and the new choice,
    and cleared only when none does. The batch list is what this is judged
    against: it is the one list carrying vendor, plant, material and batch
    together. A dimension a row does not carry cannot contradict it.
  */
  const FILTER_COLUMN = {
    cmo: "vendorMosymphonyID",
    plant: "vendorPlant",
    storageLocation: "storageLocationID",
    material: "materialCode",
    batch: "inventoryBatchNumber",
  };

  const stillHeldWith = (changedDim, changedValue, otherDim, otherValue) => {
    if (otherValue === null) return true;
    const rows = lotNumbersList?.data || [];
    if (rows.length === 0) return true;
    const a = FILTER_COLUMN[changedDim];
    const b = FILTER_COLUMN[otherDim];
    return rows.some(
      (row) =>
        (row[a] === undefined || row[a] === changedValue) &&
        (row[b] === undefined || row[b] === otherValue)
    );
  };

  /*
    The batch list is the only one carrying all four of vendor, plant, material
    and batch, so it is what the others are narrowed through. The plant list
    holds no material code and the material list holds no batch number, so
    filtering each on its own rows could only ever narrow it by the one or two
    dimensions it happens to carry -- which is why Plant was never narrowed by
    a material, and Material never by a batch.

    valuesHeld returns the values of one column that survive every filter
    except its own, or null when the batch list has not arrived, in which case
    nothing is narrowed through it rather than everything being emptied.
  */
  const inventoryRows = lotNumbersList?.data || [];
  const valuesHeld = (column, except) => {
    if (inventoryRows.length === 0) return null;
    return new Set(
      inventoryRows.filter((row) => rowMatchesFilters(row, except)).map((row) => row[column])
    );
  };
  const keepIn = (allowed, key) => (row) => allowed === null || allowed.has(row[key]);

  /*
    The CMO list cannot be filtered on its own rows at all: CMOMembersWithId
    carries organisation ids, not inventory columns.
  */
  const allCmoOptions = cmosList?.CMOMembersWithId || [];
  const cmosHoldingStock = valuesHeld("vendorMosymphonyID", "cmo");
  const cmoOptions = allCmoOptions.filter(
    (cmo) => cmosHoldingStock === null || cmosHoldingStock.has(cmo.organizationId)
  );

  /*
    The plant list names its column plantCode, not vendorPlant -- the action
    builds { plantCode, vendorMosymphonyID } from receivingPlantIDs. Keying the
    membership test and the de-duplication on vendorPlant asked every row for a
    field it does not have, so the test was has(undefined) and the dropdown came
    back empty once the batch list had loaded. The values held still come from
    the batch list, which does call the column vendorPlant.
  */
  const plantsHeld = valuesHeld("vendorPlant", "plant");
  const filteredPlantCodes = distinctBy(
    (plantCodesList?.data || [])
      .filter((row) => rowMatchesFilters(row, "plant"))
      .filter(keepIn(plantsHeld, "plantCode")),
    "plantCode"
  );

  /*
    Storage locations carry vendor and plant but no material or batch, so a
    selected material narrows them as far as the plants that material is held
    at. That can still offer a second location at the same plant; offering one
    too many is the safe side of a rule the data cannot express exactly.
  */
  const filteredStorageLocationIDs = distinctBy(
    (storageLocationList?.data || [])
      .filter((row) => rowMatchesFilters(row, "storageLocation"))
      .filter(keepIn(valuesHeld("vendorPlant", "storageLocation"), "vendorPlant")),
    "storageLocationID"
  );


  /*
    Material Code and Batch Number follow the same rule as the three above.
    They stay in effects because they are held in state rather than derived,
    and an empty result is now written through: the list that stood before was
    left on screen by `if (filteredData.length > 0)`, so a selection with no
    materials behind it still offered the previous selection's.
  */
  useEffect(() => {
    // The material list holds no batch number, so a selected batch narrows it
    // through the batch list, which holds both.
    const materialsHeld = valuesHeld("materialCode", "material");
    setFilteredMaterialCodes(
      distinctBy(
        (materialCodesList?.data || [])
          .filter((row) => rowMatchesFilters(row, "material"))
          .filter(keepIn(materialsHeld, "materialCode")),
        "materialCode"
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    materialCodesList,
    lotNumbersList,
    activeFilters.cmo,
    activeFilters.plant,
    activeFilters.batch,
  ]);

  useEffect(() => {
    setFilteredBatchNumbers(
      distinctBy(
        (lotNumbersList?.data || []).filter((row) => rowMatchesFilters(row, "batch")),
        "inventoryBatchNumber"
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    lotNumbersList,
    activeFilters.cmo,
    activeFilters.plant,
    activeFilters.material,
  ]);


  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleViewInventoryToggle = () => {
    let val = inventoryToggleValue === "BMS" ? "CMO" : "BMS"
    setInventoryToggleValue(val)
    /*
      BCM-881 — the two dashboards are served by different endpoints, so
      switching has to fetch. It did not before, when one response carried
      both views and the toggle only changed what was drawn from it.

      cmoView is passed rather than read, because setInventoryToggleValue
      above has not been applied yet. Page 1 because the result set is a
      different one, so the page count the pager is holding no longer applies.
    */
    setCurrentPage(1);
    loadInventory({ pageNumber: 1, pageSize: rowsPerPage, cmoView: val === "CMO" });
  }

  const handleRowToggle = (index) => {
    setOpenRows((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  /* ---------------------------------------------------------------------
     BCM-881 — what the table shows
     --------------------------------------------------------------------- */

  // The CMO Inventory Status dashboard, reached through "View CMO Inventory".
  const isCmoView = inventoryToggleValue === "CMO";

  /*
    The grid is re-measured whenever the block above it can change height: the
    table appearing, the view switching, the status columns being hidden and
    the loader giving way to rows.
  */
  const tableContainerRef = React.useRef(null);
  const gridHeight = useAvailableHeight(tableContainerRef, [
    showTable,
    isCmoView,
    showTotalQtyOnly,
    groupRows,
    inventoryDataLoading,
  ]);

  /*
    BCM-881 — "Where no BMS batch is mapped, the group is the material code
    reported by the CMO, followed by (CMO) in brackets."

    The CMO dashboard lists every batch the CMO reports, including ones no BMS
    batch is mapped to. Those rows carry the CMO's own material code, and
    without the suffix it reads as a BMS material code -- the column is headed
    "BMS Material Code / Description".

    A CMO batch with no BMS batch behind it arrives with no lotNumber, so a
    material is the CMO's own when none of its batches has one. A material with
    even one mapped batch is a BMS material and is left alone.
  */
  const isCmoOnlyMaterial = (vendor) => {
    if (!isCmoView) return false;
    const lots = Array.isArray(vendor?.lotNumbersList) ? vendor.lotNumbersList : [];
    if (!lots.length) return false;
    return lots.every((lot) => !lot?.lotNumber);
  };

  const materialLabel = (vendor) =>
    `${vendor?.materialCode || ""}${isCmoOnlyMaterial(vendor) ? " (CMO)" : ""} / ${vendor?.materialDescription || ""}`;

  /*
    The FSD covers the sponsor's two dashboards — BMS Inventory Status and the
    CMO Inventory Status reached through "View CMO Inventory", a button that is
    itself shown only to a sponsor. A CMO login lands on neither: it sees its
    own inventory, with inventoryToggleValue left null. Every column, option
    and tooltip added by BCM-881 is therefore gated on this, so the CMO login's
    screen is exactly what it is today.
  */
  const bcm881Enabled = localStorage.collabType !== "CMO";

  // The FSD gates the CMO status columns and the "Show CMO total qty. only"
  // checkbox on a single CMO being chosen: with the filter on All, the statuses
  // configured differ from CMO to CMO and there is no one set to show. It is the
  // CMO the rows on screen were fetched for, not the dropdown's live value, so
  // the columns never describe a CMO whose data is not shown.
  const isSingleCmoSelected = !!appliedCmoId && appliedCmoId !== "All";

  /*
    Every quantityBasedOnStatus[] row on the page, in the order met. Corda builds
    each one from the CMO's file (statusName, quantity) joined to the CMO
    Inventory Mapping (renamedColumnName, partOfCMOTotalQty,
    partOfCMOUnrestrictedQty), so this array is the single source for the CMO
    status columns and the CMO total / Unrestricted tooltips.
  */
  const cmoStatusRows = React.useMemo(() => {
    if (!isCmoView || !isSingleCmoSelected) return [];
    const rows = [];
    (paginatedData || []).forEach((vendor) => {
      (vendor?.lotNumbersList || []).forEach((lot) => {
        cmoBatchListOf(lot).forEach((batch) => {
          (batch?.quantityBasedOnStatus || []).forEach((entry) => {
            if (entry) rows.push(entry);
          });
        });
      });
    });
    return rows;
  }, [isCmoView, isSingleCmoSelected, paginatedData]);

  /*
    The status columns for the CMO in view (FSD: one per mapping row with
    Selection on, headed by its Renamed Status/Column name; only when a single
    CMO is selected).

    Corda sends renamedColumnName only for statuses mapped and switched on, so
    those rows are the columns and nothing else is. A CMO with no mapping saved
    therefore gets no status columns at all, and an unmapped status inside a
    configured CMO is not drawn either -- both confirmed with the product team.
    The raw statusName was shown as a fallback before that; it is gone, because
    a CMO's own file wording is what the mapping exists to replace.
  */
  const cmoStatusColumns = React.useMemo(() => {
    const columns = new Map();
    cmoStatusRows.forEach((entry) => {
      const label = entry.renamedColumnName;
      if (!label) return;
      const column = columns.get(label) || { key: label, label, aliases: [label] };
      if (entry.statusName && !column.aliases.includes(entry.statusName)) {
        column.aliases.push(entry.statusName);
      }
      columns.set(label, column);
    });
    return Array.from(columns.values());
  }, [cmoStatusRows]);

  // The four BMS statuses are fixed by the FSD; the CMO ones come from config.
  const statusColumns = !bcm881Enabled
    ? []
    : isCmoView
      ? cmoStatusColumns
      : BMS_STATUS_COLUMNS;

  /*
    The CMO total qty. and CMO Unrestricted Qty. tooltips, generated from the
    flags on quantityBasedOnStatus[] as the FSD asks. With All CMOs in view, or
    no row flagged, the FSD's general wording stands.
  */
  const cmoQuantityTooltips = React.useMemo(
    () => ({
      cmoTotalQty:
        cmoQuantityTooltip(cmoStatusRows, "partOfCMOTotalQty") || TOOLTIP_TEXT.cmoTotalQty,
      cmoUnrestrictedQty:
        cmoQuantityTooltip(cmoStatusRows, "partOfCMOUnrestrictedQty") ||
        TOOLTIP_TEXT.cmoUnrestrictedQty,
    }),
    [cmoStatusRows]
  );

  /*
    One header cell. Pulled out because BCM-881 takes the table from 11 columns
    to as many as 20, and the repeated CustomTableCell boilerplate made the
    column order impossible to read at a glance.
  */
  const headerCell = (label, align = "left", tooltip = null, key = null, style = null) => (
    <CustomTableCell
      key={key || label}
      scope="row"
      align={align}
      elementStyle={{
        width: "auto",
        fontWeight: "bold",
        // The table now scrolls horizontally rather than compressing, so a
        // label no longer has to wrap; every header sits on one line and on
        // one baseline. The material column passes MATERIAL_CELL_STYLE and
        // keeps its own wrapping through the spread below.
        whiteSpace: "nowrap",
        verticalAlign: "bottom",
        ...style,
      }}
    >
      {label}
      {tooltip ? (
        <InfoTip title={tooltip} className={classes.infoIcon} />
      ) : null}
    </CustomTableCell>
  );

  // "Show total qty. only" hides the breakup and leaves the totals in place.
  const showStatusColumns = statusColumns.length > 0 && !showTotalQtyOnly;

  // On the CMO dashboard the checkbox is only offered for a single CMO.
  const totalQtyOnlyDisabled = isCmoView && !isSingleCmoSelected;

  const totalQtyOnlyHint = isCmoView
    ? isSingleCmoSelected
      ? "Hides the status-level breakup columns."
      : "Available only when a single CMO is selected."
    : "Hides the SAP status columns.";

  /*
    Clearing "Group rows" lists every batch as its own row, so the chevron
    column and the material level parent row go away. Keep the material level
    expansion state tidy when the user switches back and forth.
  */
  const handleGroupRowsChange = (event) => {
    const checked = event.target.checked;
    setGroupRows(checked);
    if (!checked) setOpenRows({});
  };

  const handleTotalQtyOnlyChange = (event) => {
    setShowTotalQtyOnly(event.target.checked);
  };

  const handleCmoBatchesToggle = (rowKey) => {
    setOpenCmoBatches((prev) => ({ ...prev, [rowKey]: !prev[rowKey] }));
  };

  /*
    The CMO batches mapped to one BMS batch. The FSD shows the first CMO batch
    followed by a "+1", "+2" … control opening the rest. The inventory API
    carries a single cmoLotNumber per row today, so until it carries the list
    this returns the one batch and no control is rendered.
  */
  const cmoBatchesOf = (lot) => {
    const list = cmoBatchListOf(lot);
    if (list.length > 0) return list;
    // the pre-BCM-881 shape: one CMO batch carried on the lot itself
    if (readField(lot, FIELD.cmoBatchNumber)) return [lot];
    return [];
  };

  /* ---------------------------------------------------------------------
     BCM-881 — inventory timestamps shown on the controls row
     --------------------------------------------------------------------- */

  /*
    The "Last synced @ …" line that used to stand here printed
    moment(new Date()) — the browser's own clock, re-evaluated on every render,
    so it was never a sync time. It now reads the sync time the API returns,
    which is the same value the "(Last updated on …)" note beside the heading
    is built from.

    Corda sends these times in UTC without a zone, so they go through
    convertToLocaleDateTime (UTC -> the user's time zone) like the heading note.
    Read with plain moment() they were taken as local time and showed 5h30m
    behind the note in IST.
  */
  const toLocalMoment = (value) => {
    if (!value) return null;
    const local = moment(convertToLocaleDateTime(value), "DD-MMM-YYYY HH:mm:ss", "en");
    return local.isValid() ? local : null;
  };

  const lastSyncedMoment = toLocalMoment(inventorySyncTime);
  const lastSyncedLabel = lastSyncedMoment
    ? `Last synced @ ${lastSyncedMoment.format("HH:mm:ss; DD MMMM, YYYY")}`
    : "Last synced @ NA";

  const formatInventoryTimestamp = (value) => {
    const local = toLocalMoment(value);
    return local ? local.format("DD-MMM-YYYY HH:mm:ss").toUpperCase() : "";
  };

  const bmsInventoryTimestamp = formatInventoryTimestamp(
    inventoryData?.sponsorLatestInventorySyncTime
  );
  const cmoInventoryTimestamp = formatInventoryTimestamp(
    inventoryData?.cmoLatestInventorySyncTime
  );

  const isSaveDisabled =
    (selectedCMO?.organizationName || "all") === savedFilters.vendorName &&
    (selectedPlant?.plantCode || "all") === savedFilters.vendorPlant &&
    (selectedMaterialCode?.materialCode || "all") ===
    savedFilters.rmpmCodeDescription &&
    (selectedBatchNumber?.inventoryBatchNumber || "all") ===
    savedFilters.sponsorLotNumber;

  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = event.target.value;
    setRowsPerPage(newRowsPerPage);

    setCurrentPage(1); //Reset to first page
    // pageNumber stays currentPage, as it has always been here: setCurrentPage
    // above has not been applied yet, and this preserves that behaviour.
    loadInventory({ pageNumber: currentPage, pageSize: newRowsPerPage });
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    // paginateData();
    loadInventory({ pageNumber: newPage, pageSize: rowsPerPage });
  };

  /*
    First value that is actually present. The columns BCM-881 adds are not
    carried by the inventory API yet, and the field they will arrive under is
    not settled, so each cell reads the candidates in turn and falls back to
    blank rather than rendering "undefined".
  */
  const pick = (...values) => {
    for (const value of values) {
      if (value !== null && value !== undefined && value !== "") return value;
    }
    return "";
  };

  const formatDateCell = (value) =>
    value ? moment(value).format("DD-MMM-YYYY").toUpperCase() : "";

  // The red/yellow expiry flags already shown against a batch number.
  const expiryFlag = (expired, nearExpiry) => {
    if (expired === true) {
      return (
        <FlagIcon
          style={{
            color: "red",
            fontSize: 28,
            stroke: "black",
            strokeWidth: 1.3,
          }}
        />
      );
    }
    if (nearExpiry) {
      return (
        <FlagIcon
          style={{
            color: "yellow",
            fontSize: 28,
            stroke: "black",
            strokeWidth: 1.3,
          }}
        />
      );
    }
    return null;
  };

  /*
    BCM-881 — how many columns the table currently has. The loading spinner and
    the "No records found" row span the table, and both carried a hard coded
    colSpan that was already short of the real count before this ticket and
    would now be well short of it.
  */
  const totalColumnCount =
    (groupRows ? 1 : 0) +
    1 +
    (bcm881Enabled ? 1 : 0) +
    (!isCmoView || localStorage.collabType === "CMO" ? 2 : 0) +
    (bcm881Enabled ? 2 : 0) +
    2 +
    (isCmoView ? 1 : 0) +
    (showStatusColumns ? statusColumns.length : 0) +
    1 +
    (isCmoView ? 1 : 0) +
    (isCmoView && bcm881Enabled ? 1 : 0) +
    2 +
    1 +
    (localStorage.collabType !== "CMO" && inventoryToggleValue === "BMS"
      ? 1
      : 0) +
    1 +
    (isCmoView && bcm881Enabled ? 1 : 0);

  /*
    One batch row — used both for the batches opened under a material in the
    grouped view and, when "Group rows" is cleared, for every batch as a row of
    its own. The two were the same markup before BCM-881; they are now one
    renderer so the flat view cannot drift from the expanded one.
  */
  /*
    BCM-881 — the CMO batch rows that the "+1" control opens.

    Both the material row and the batch row can carry that control, so both
    need to be able to draw these. It used to live inside the batch row only,
    while the material row's button wrote to a key of its own -- so on a
    material holding a single BMS batch, which is drawn as a material row with
    no batch row beneath it, the button had nothing to open and clicking it
    did nothing.

    The BMS batch number, its quantity and its expiry date stay on the row
    above and are not repeated here, as the FSD requires.
  */
  const renderCmoBatchRows = (cmoBatches, rowKey) =>
    cmoBatches.map((cmoBatch, cmoIndex) => (
            <TableRow
              key={`${rowKey}-cmo-batch-${cmoIndex}`}
              className={classes.collapsedRow}
            >
              {groupRows && <TableCell style={{ width: "1%", maxWidth: 0 }} />}
              <CustomTableCell scope="row" align="left" />
              {bcm881Enabled && <CustomTableCell scope="row" align="left" />}
              {(inventoryToggleValue !== "CMO" ||
                localStorage.collabType === "CMO") && (
                  <CustomTableCell scope="row" align="left" />
                )}
              {(inventoryToggleValue !== "CMO" ||
                localStorage.collabType === "CMO") && (
                  <CustomTableCell scope="row" align="left" />
                )}
              {bcm881Enabled && <CustomTableCell scope="row" align="left" />}
              {bcm881Enabled && <CustomTableCell scope="row" align="left" />}
              <CustomTableCell scope="row" align="left" />
              <CustomTableCell scope="row" align="left" />
              <CustomTableCell
                scope="row"
                align="left"
                elementStyle={{ whiteSpace: "nowrap" }}
              >
                {readField(cmoBatch, FIELD.cmoBatchNumber) || ""}
                {expiryFlag(
                  cmoBatch?.cmoBatchExpired,
                  cmoBatch?.cmoBatchNearExpiry
                )}
              </CustomTableCell>
              {/*
                Column order has to match the header, which runs BMS total qty.,
                then the CMO status columns, then CMO total qty. These rows had
                the blank BMS total cell after the status columns instead of
                before them: the cell count still matched, so nothing looked
                broken, but every status quantity landed one column to the left
                and the last status column read blank. renderLotRow above has
                the right order; this is the same order.
              */}
              <CustomTableCell scope="row" align="right" />
              {showStatusColumns &&
                statusColumns.map((col) => (
                  <CustomTableCell
                    key={`${rowKey}-cmo-batch-${cmoIndex}-${col.key}`}
                    scope="row"
                    align="right"
                    elementStyle={{ whiteSpace: "nowrap" }}
                  >
                    {formatQty(cmoStatusQty(cmoBatch, col), { blankWhenMissing: true })}
                  </CustomTableCell>
                ))}
              <CustomTableCell
                scope="row"
                align="right"
                elementStyle={{ whiteSpace: "nowrap", fontWeight: 500 }}
              >
                {formatQty(readField(cmoBatch, FIELD.cmoTotalQty))}
              </CustomTableCell>
              {bcm881Enabled && (
                <CustomTableCell
                  scope="row"
                  align="right"
                  elementStyle={{ whiteSpace: "nowrap" }}
                >
                  {cmoUnrestrictedQty(cmoBatch) === undefined
                    ? "NA"
                    : formatQty(cmoUnrestrictedQty(cmoBatch))}
                </CustomTableCell>
              )}
              <CustomTableCell scope="row" align="right" />
              <CustomTableCell scope="row" align="right" />
              <CustomTableCell scope="row" align="left" />
              {localStorage.collabType !== "CMO" &&
                inventoryToggleValue === "BMS" && (
                  <CustomTableCell scope="row" align="left" />
                )}
              <CustomTableCell scope="row" align="left" />
              {bcm881Enabled && (
                <CustomTableCell
                  scope="row"
                  align="left"
                  elementStyle={{ whiteSpace: "nowrap", overflow: "visible" }}
                >
                  {formatDateCell(readField(cmoBatch, FIELD.cmoExpiryDate))}
                </CustomTableCell>
              )}
            </TableRow>
    ));

  const renderLotRow = (vendor, lot, rowKey) => {
    const cmoBatches = cmoBatchesOf(lot);
    const hasMoreCmoBatches = cmoBatches.length > 1;
    const cmoBatchesOpen = !!openCmoBatches[rowKey];

    return (
      <React.Fragment key={rowKey}>
        <TableRow>
          {groupRows && (
            <TableCell
              style={{
                width: "1%",
                maxWidth: 0,
                whiteSpace: "nowrap",
              }}
            />
          )}
          <CustomTableCell
            scope="row"
            align="left"
            elementStyle={{ whiteSpace: "nowrap" }}
          >
            {pick(lot.vendorName, vendor.vendorName)}
          </CustomTableCell>
          {bcm881Enabled && (
            <CustomTableCell
              scope="row"
              align="left"
              elementStyle={{ whiteSpace: "nowrap" }}
            >
              {readField(lot, FIELD.cmoID) ?? readField(vendor, FIELD.cmoID) ?? ""}
            </CustomTableCell>
          )}
          {(inventoryToggleValue !== "CMO" ||
            localStorage.collabType === "CMO") && (
              <CustomTableCell
                scope="row"
                align="left"
                elementStyle={{ whiteSpace: "nowrap" }}
              >
                {lot.vendorPlant}
              </CustomTableCell>
            )}
          {(inventoryToggleValue !== "CMO" ||
            localStorage.collabType === "CMO") && (
              <CustomTableCell
                scope="row"
                align="left"
                elementStyle={{ whiteSpace: "nowrap" }}
              >
                {/* Storage Location ID — per-lot value, not the parent group's */}
                {lot.storageLocationID}
              </CustomTableCell>
            )}
          {bcm881Enabled && (
            <CustomTableCell
              scope="row"
              align="left"
              elementStyle={{ whiteSpace: "nowrap" }}
            >
              {pick(lot.brand, vendor.brand)}
            </CustomTableCell>
          )}
          {bcm881Enabled && (
            <CustomTableCell
              scope="row"
              align="left"
              elementStyle={{ whiteSpace: "nowrap" }}
            >
              {pick(lot.materialType, vendor.materialType)}
            </CustomTableCell>
          )}
          <CustomTableCell
            scope="row"
            align="left"
            elementStyle={MATERIAL_CELL_STYLE}
          >
            {materialLabel(vendor)}
          </CustomTableCell>
          <CustomTableCell
            scope="row"
            align="left"
            elementStyle={{ whiteSpace: "nowrap" }}
          >
            {lot.lotNumber || ""}
            {expiryFlag(readField(lot, FIELD.batchExpired), readField(lot, FIELD.batchNearExpiry))}
          </CustomTableCell>

          {/* CMO Batch Number */}
          {isCmoView && (
            <CustomTableCell
              scope="row"
              align="left"
              elementStyle={{ whiteSpace: "nowrap" }}
            >
              {readField(lot, FIELD.cmoBatchNumber) || ""}
              {expiryFlag(lot?.cmoBatchExpired, lot?.cmoBatchNearExpiry)}
              {/* BCM-881: where more than one CMO batch is mapped to this BMS
                  batch, the first is shown followed by a "+1", "+2" … control
                  that opens the rest beneath. */}
              {hasMoreCmoBatches && (
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  disableElevation
                  className={classes.moreBatchesButton}
                  onClick={() => handleCmoBatchesToggle(rowKey)}
                >
                  {`+${cmoBatches.length - 1}`}
                </Button>
              )}
            </CustomTableCell>
          )}

          {!isCmoView &&
            showStatusColumns &&
            statusColumns.map((col) => (
              <CustomTableCell
                key={`${rowKey}-bms-${col.key}`}
                scope="row"
                align="right"
                elementStyle={{ whiteSpace: "nowrap" }}
              >
                {formatQty(readField(lot, col.fields), { blankWhenMissing: true })}
              </CustomTableCell>
            ))}

          <CustomTableCell
            scope="row"
            align="right"
            elementStyle={{
              whiteSpace: "nowrap",
              // BCM-881 emphasises the total; a CMO login's column is
              // untouched, so it keeps the normal weight.
              fontWeight: bcm881Enabled ? 500 : undefined,
            }}
          >
            {bcm881Enabled
              ? bmsTotalFromColumns(lot, { blankWhenNoBmsBatch: isCmoView })
              : formatQty(readField(lot, FIELD.bmsTotalQty))}
          </CustomTableCell>

          {isCmoView &&
            showStatusColumns &&
            statusColumns.map((col) => (
              <CustomTableCell
                key={`${rowKey}-cmo-${col.key}`}
                scope="row"
                align="right"
                elementStyle={{ whiteSpace: "nowrap" }}
              >
                {formatQty(cmoStatusQty(lot, col), { blankWhenMissing: true })}
              </CustomTableCell>
            ))}

          {isCmoView && (
            <CustomTableCell
              scope="row"
              align="right"
              elementStyle={{ whiteSpace: "nowrap", fontWeight: 500 }}
            >
              {formatQty(readField(lot, FIELD.cmoTotalQty))}
            </CustomTableCell>
          )}
          {isCmoView && bcm881Enabled && (
            <CustomTableCell
              scope="row"
              align="right"
              elementStyle={{ whiteSpace: "nowrap" }}
            >
              {/* BCM-881: NA until the CMO reports an unrestricted quantity —
                  the FSD's stated behaviour for Patheon until the inventory
                  IDocs arrive. */}
              {cmoUnrestrictedQty(lot) === undefined
                ? "NA"
                : formatQty(cmoUnrestrictedQty(lot))}
            </CustomTableCell>
          )}

          <CustomTableCell
            scope="row"
            align="right"
            elementStyle={{ whiteSpace: "nowrap" }}
          >
            {formatQty(readField(lot, FIELD.netQtyAvailable), {
              blankWhenMissing: isCmoView && hasNoBmsBatch(lot),
            })}
          </CustomTableCell>
          <CustomTableCell
            scope="row"
            align="right"
            elementStyle={{ whiteSpace: "nowrap" }}
          >
            {formatQty(readField(lot, FIELD.allocatedQty), {
              blankWhenMissing: isCmoView && hasNoBmsBatch(lot),
            })}
          </CustomTableCell>
          <CustomTableCell
            scope="row"
            align="left"
            elementStyle={{ whiteSpace: "nowrap" }}
          >
            {readField(lot, FIELD.uom) || ""}
          </CustomTableCell>
          {localStorage.collabType !== "CMO" &&
            inventoryToggleValue === "BMS" && (
              <CustomTableCell
                scope="row"
                align="left"
                elementStyle={{ whiteSpace: "nowrap", overflow: "visible" }}
              >
                {formatDateCell(readField(lot, FIELD.manufacturingDate))}
              </CustomTableCell>
            )}
          <CustomTableCell
            scope="row"
            align="left"
            elementStyle={{ whiteSpace: "nowrap", overflow: "visible" }}
          >
            {formatDateCell(readField(lot, FIELD.expiryDate))}
          </CustomTableCell>
          {isCmoView && bcm881Enabled && (
            <CustomTableCell
              scope="row"
              align="left"
              elementStyle={{ whiteSpace: "nowrap", overflow: "visible" }}
            >
              {/* BCM-881: one CMO batch shows its own expiry; several show
                  "see batches", with each date against the batch when the
                  rows are opened. */}
              {hasMoreCmoBatches ? (
                <span style={{ color: "#757575" }}>see batches</span>
              ) : (
                formatDateCell(readField(lot, FIELD.cmoExpiryDate) ?? readField(cmoBatches[0], FIELD.cmoExpiryDate))
              )}
            </CustomTableCell>
          )}
        </TableRow>

        {/*
          BCM-881 — the CMO batches held against this BMS batch, opened by the
          "+N" control. Each carries its own CMO batch number, CMO total qty.,
          CMO Unrestricted Qty. and CMO expiry date. The BMS batch number, the
          BMS quantities and the BMS expiry date are shown once on the row
          above and are not repeated here.
        */}
        {isCmoView && hasMoreCmoBatches && cmoBatchesOpen &&
          renderCmoBatchRows(cmoBatches, rowKey)}
      </React.Fragment>
    );
  };

  /*
    The material level row in the grouped view. Where the material holds a
    single batch the row carries that batch's values, and where it holds
    several the quantity cells are left blank and the batches are opened with
    the chevron — the behaviour this table already had.
  */
  const renderGroupRow = (vendor, index, lots) => {
    const one = lots.length === 1 ? lots[0] : null;
    const cmoBatches = one ? cmoBatchesOf(one) : [];
    const hasMoreCmoBatches = cmoBatches.length > 1;

    return (
      <React.Fragment>
        <TableRow style={{ width: "100%" }}>
        <TableCell
          size="small"
          style={{
            maxWidth: "0",
            whiteSpace: "nowrap",
            width: "1%",
            padding: 0,
          }}
        >
          {lots.length > 1 && (
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => handleRowToggle(index)}
            >
              {openRows[index] ? <ExpandMoreIcon /> : <ChevronRightIcon />}
            </IconButton>
          )}
        </TableCell>
        <CustomTableCell
          scope="row"
          align="left"
          elementStyle={{ whiteSpace: "nowrap" }}
        >
          {localStorage.collabType === "CMO"
            ? vendor.brandOwnerName
            : vendor.vendorName}
        </CustomTableCell>
        {bcm881Enabled && (
          <CustomTableCell
            scope="row"
            align="left"
            elementStyle={{ whiteSpace: "nowrap" }}
          >
            {readField(vendor, FIELD.cmoID) ?? readField(one, FIELD.cmoID) ?? ""}
          </CustomTableCell>
        )}
        {(inventoryToggleValue !== "CMO" ||
          localStorage.collabType === "CMO") && (
            <CustomTableCell
              scope="row"
              align="left"
              elementStyle={{ whiteSpace: "nowrap" }}
            >
              {vendor.vendorPlant}
            </CustomTableCell>
          )}
        {(inventoryToggleValue !== "CMO" ||
          localStorage.collabType === "CMO") && (
            <CustomTableCell
              scope="row"
              align="left"
              elementStyle={{ whiteSpace: "nowrap" }}
            >
              {/* Storage Location ID — single lot: use the lot's own value;
                  multiple lots (collapsed): fall back to the group value */}
              {one ? one?.storageLocationID : vendor.storageLocationID}
            </CustomTableCell>
          )}
        {bcm881Enabled && (
          <CustomTableCell
            scope="row"
            align="left"
            elementStyle={{ whiteSpace: "nowrap" }}
          >
            {pick(vendor.brand, one?.brand)}
          </CustomTableCell>
        )}
        {bcm881Enabled && (
          <CustomTableCell
            scope="row"
            align="left"
            elementStyle={{ whiteSpace: "nowrap" }}
          >
            {pick(vendor.materialType, one?.materialType)}
          </CustomTableCell>
        )}
        <CustomTableCell
          scope="row"
          align="left"
          elementStyle={MATERIAL_CELL_STYLE}
        >
          {materialLabel(vendor)}
        </CustomTableCell>
        <CustomTableCell
          scope="row"
          align="left"
          elementStyle={{ whiteSpace: "nowrap" }}
        >
          {one && (
            <React.Fragment>
              {one.lotNumber || ""}
              {expiryFlag(readField(one, FIELD.batchExpired), readField(one, FIELD.batchNearExpiry))}
            </React.Fragment>
          )}
        </CustomTableCell>
        {isCmoView && (
          <CustomTableCell
            scope="row"
            align="left"
            elementStyle={{ whiteSpace: "nowrap" }}
          >
            {one && (
              <React.Fragment>
                {readField(one, FIELD.cmoBatchNumber) || ""}
                {expiryFlag(one?.cmoBatchExpired, one?.cmoBatchNearExpiry)}
                {hasMoreCmoBatches && (
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    disableElevation
                    className={classes.moreBatchesButton}
                    onClick={() => handleCmoBatchesToggle(`group-${index}`)}
                  >
                    {`+${cmoBatches.length - 1}`}
                  </Button>
                )}
              </React.Fragment>
            )}
          </CustomTableCell>
        )}

        {!isCmoView &&
          showStatusColumns &&
          statusColumns.map((col) => (
            <CustomTableCell
              key={`group-${index}-bms-${col.key}`}
              scope="row"
              align="right"
              elementStyle={{ whiteSpace: "nowrap" }}
            >
              {one ? formatQty(readField(one, col.fields), { blankWhenMissing: true }) : ""}
            </CustomTableCell>
          ))}

        <CustomTableCell
          scope="row"
          align="right"
          elementStyle={{
            whiteSpace: "nowrap",
            fontWeight: bcm881Enabled ? 500 : undefined,
          }}
        >
          {one
            ? bcm881Enabled
              ? bmsTotalFromColumns(one, { blankWhenNoBmsBatch: isCmoView })
              : formatQty(readField(one, FIELD.bmsTotalQty))
            : ""}
        </CustomTableCell>

        {isCmoView &&
          showStatusColumns &&
          statusColumns.map((col) => (
            <CustomTableCell
              key={`group-${index}-cmo-${col.key}`}
              scope="row"
              align="right"
              elementStyle={{ whiteSpace: "nowrap" }}
            >
              {one ? formatQty(cmoStatusQty(one, col), { blankWhenMissing: true }) : ""}
            </CustomTableCell>
          ))}

        {isCmoView && (
          <CustomTableCell
            scope="row"
            align="right"
            elementStyle={{ whiteSpace: "nowrap", fontWeight: 500 }}
          >
            {one ? formatQty(readField(one, FIELD.cmoTotalQty)) : ""}
          </CustomTableCell>
        )}
        {isCmoView && bcm881Enabled && (
          <CustomTableCell
            scope="row"
            align="right"
            elementStyle={{ whiteSpace: "nowrap" }}
          >
            {one
              ? cmoUnrestrictedQty(one) === undefined
                ? "NA"
                : formatQty(cmoUnrestrictedQty(one))
              : ""}
          </CustomTableCell>
        )}

        <CustomTableCell
          scope="row"
          align="right"
          elementStyle={{ whiteSpace: "nowrap" }}
        >
          {one
            ? formatQty(readField(one, FIELD.netQtyAvailable), {
                blankWhenMissing: isCmoView && hasNoBmsBatch(one),
              })
            : ""}
        </CustomTableCell>
        <CustomTableCell
          scope="row"
          align="right"
          elementStyle={{ whiteSpace: "nowrap" }}
        >
          {one
            ? formatQty(readField(one, FIELD.allocatedQty), {
                blankWhenMissing: isCmoView && hasNoBmsBatch(one),
              })
            : ""}
        </CustomTableCell>
        <CustomTableCell
          scope="row"
          align="left"
          elementStyle={{ whiteSpace: "nowrap" }}
        >
          {one ? readField(one, FIELD.uom) || "" : ""}
        </CustomTableCell>
        {localStorage.collabType !== "CMO" &&
          inventoryToggleValue === "BMS" && (
            <CustomTableCell
              scope="row"
              align="left"
              elementStyle={{ whiteSpace: "nowrap" }}
            >
              {one ? formatDateCell(readField(one, FIELD.manufacturingDate)) : ""}
            </CustomTableCell>
          )}
        <CustomTableCell
          scope="row"
          align="left"
          /* No maxWidth 0 here: on a table cell it caps the whole column, so
             the batch rows' dates below spilled past the column and the end
             of the header. Plain nowrap sizes the column to the date. */
          elementStyle={{ whiteSpace: "nowrap" }}
        >
          {one ? formatDateCell(readField(one, FIELD.expiryDate)) : ""}
        </CustomTableCell>
        {isCmoView && bcm881Enabled && (
          <CustomTableCell
            scope="row"
            align="left"
            elementStyle={{ whiteSpace: "nowrap", overflow: "visible" }}
          >
            {one
              ? hasMoreCmoBatches
                ? <span style={{ color: "#757575" }}>see batches</span>
                : formatDateCell(readField(one, FIELD.cmoExpiryDate) ?? readField(cmoBatches[0], FIELD.cmoExpiryDate))
              : ""}
          </CustomTableCell>
        )}
        </TableRow>
        {/*
          BCM-881 — a material holding a single BMS batch is drawn as this row
          alone, with no batch row beneath it, so the "+1" has to open the CMO
          batches from here. It writes to a key of its own, group-<index>, which
          is why it is read back with the same one.
        */}
        {isCmoView && hasMoreCmoBatches && !!openCmoBatches[`group-${index}`] &&
          renderCmoBatchRows(cmoBatches, `group-${index}`)}
      </React.Fragment>
    );
  };

  const brandOwnerName =
    localStorage.getItem("ecosystem_name") ||
    "";

  return (
    <div>
      <Typography
        style={{ fontSize: "1rem", fontWeight: 550, marginBottom: "0.5rem" }}
      >
        Please enter any of the following values to view the report
      </Typography>

      <Grid
        container
        spacing={2}
        style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: "15px", rowGap: "15px" }}
      >
        {/* Select CMO/CDMO */}

        <Grid item xs="auto" className={classes.smallGrid}>
          {localStorage.collabType === "CMO" ? (
            <TextField
              style={{
                whiteSpace: "nowrap",
                overflowX: "auto",
                scrollbarWidth: "none", // Hide scrollbar (Firefox)
                msOverflowStyle: "none", // Hide scrollbar (IE/Edge)
              }}
              value={brandOwnerName}
              label={
                <span style={{ whiteSpace: "nowrap" }}>
                  Select BMS
                  {/* <span style={{ color: "red" }}>{" "}*</span> */}
                </span>
              }
              InputProps={{
                readOnly: true, // Make it non-editable
                style: { minWidth: "max-content" }, // Ensures full text visibility
              }}
              fullWidth
            />
          ) : (
            <Autocomplete
              style={{
                whiteSpace: "nowrap",
                overflowX: "auto",
                scrollbarWidth: "none", // Hide scrollbar (Firefox)
                msOverflowStyle: "none", // Hide scrollbar (IE/Edge)
              }}
              options={cmoOptions ? [{ organizationId: 'All', organizationName: "All" }, ...cmoOptions] : [{ organizationId: 'All', organizationName: "All" }]}
              getOptionLabel={(option) => option?.organizationName || ""}
              value={selectedCMO}
              // value={filter?.vendorName || selectedCMO}
              disabled={isStorageLocationSelected}
              onChange={(event, newValue) => {
                setSelectedCMO(newValue);
                const cmo = chosenValue(newValue?.organizationId);
                // CMO and Storage Location are mutually exclusive, as the FSD has it.
                if (cmo !== null) setSelectedStorageLocation({ storageLocationID: 'All' });
                // The rest stay unless this CMO holds nothing for them.
                if (!stillHeldWith("cmo", cmo, "plant", chosenValue(selectedPlant?.plantCode))) {
                  setSelectedPlant({ plantCode: 'All' });
                }
                if (!stillHeldWith("cmo", cmo, "material", chosenValue(selectedMaterialCode?.materialCode))) {
                  setSelectedMaterialCode({ materialCode: 'All', materialDescription: 'All' });
                }
                if (!stillHeldWith("cmo", cmo, "batch", chosenValue(selectedBatchNumber?.inventoryBatchNumber))) {
                  setSelectedBatchNumber({ inventoryBatchNumber: 'All' });
                }
              }}
              getOptionSelected={(option, value) =>
                option.organizationId === value.organizationId && option.organizationName === value.organizationName
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={
                    <span style={{ whiteSpace: "nowrap" }}>
                      Select CMO
                      {/* <span style={{ color: "red" }}>{" "}*</span> */}
                    </span>
                  }
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: <SearchIcon style={{ marginRight: 8 }} />,
                    style: { minWidth: "max-content" },
                  }}
                  fullWidth
                />
              )}
            />
          )}
        </Grid>

        {/* Select Plant ID */}

        <Grid item xs="auto" className={classes.smallGrid}>
          <Autocomplete
            style={{
              whiteSpace: "nowrap",
              overflowX: "auto",
              scrollbarWidth: "none", // Hide scrollbar (Firefox)
              msOverflowStyle: "none", // Hide scrollbar (IE/Edge)
            }}
            options={Array.from(
              new Map(
                [{ plantCode: 'All' }, ...(filteredPlantCodes || [])]
                  // BCM-234: drop null/undefined/empty AND dedupe by plantCode —
                  // the backend returns one row per plant × vendor × storage location,
                  // so the same plant repeats. Keyed Map keeps one entry per plantCode.
                  .filter((option) => option?.plantCode != null && String(option.plantCode).trim() !== '')
                  .map((option) => [option.plantCode, option])
              ).values()
            )}
            getOptionLabel={(option) => option?.plantCode || ""}
            value={selectedPlant}
            // value={filter.vendorPlant || selectedPlant}
            onChange={(event, newValue) => {
              setSelectedPlant(newValue);
              const plant = chosenValue(newValue?.plantCode);
              // A plant excludes nothing by rule; the others stay unless this
              // plant holds nothing for them.
              if (!stillHeldWith("plant", plant, "storageLocation", chosenValue(selectedStorageLocation?.storageLocationID))) {
                setSelectedStorageLocation({ storageLocationID: 'All' });
              }
              if (!stillHeldWith("plant", plant, "material", chosenValue(selectedMaterialCode?.materialCode))) {
                setSelectedMaterialCode({ materialCode: 'All', materialDescription: 'All' });
              }
              if (!stillHeldWith("plant", plant, "batch", chosenValue(selectedBatchNumber?.inventoryBatchNumber))) {
                setSelectedBatchNumber({ inventoryBatchNumber: 'All' });
              }
            }}
            getOptionSelected={(option, value) =>
              option.plantCode === value.plantCode
            }
            // disabled={!selectedCMO}
            renderInput={(params) => (
              <TextField
                {...params}
                label={
                  <span style={{ whiteSpace: "nowrap" }}>
                    Select Plant ID
                    {/* <span style={{ color: "red" }}>*</span> */}
                  </span>
                }
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <SearchIcon style={{ marginRight: 8 }} />,
                }}
                fullWidth
              />
            )}
          />
        </Grid>

        {/* Select Storage Location Id */}

        <Grid item xs="auto" className={classes.smallGrid}>
          <Autocomplete
            style={{
              whiteSpace: "nowrap",
              overflowX: "auto",
              scrollbarWidth: "none", // Hide scrollbar (Firefox)
              msOverflowStyle: "none", // Hide scrollbar (IE/Edge)
            }}
            options={Array.from(
              new Map(
                [{ storageLocationID: 'All' }, ...(filteredStorageLocationIDs || [])]
                  // BCM-234: drop null/undefined/empty ("" and whitespace) AND dedupe —
                  // the API returns many rows with blank/null storageLocationID, which
                  // otherwise render as an empty gray option under "All".
                  .filter((option) => option?.storageLocationID != null && String(option.storageLocationID).trim() !== '')
                  .map((option) => [option.storageLocationID, option])
              ).values()
            )}
            getOptionLabel={(option) => option?.storageLocationID || ""}
            value={selectedStorageLocation}
            /*
              BCM-881 — "The Storage Location ID filter is disabled at all
              times, as the dashboard shows inventory held at Special Stock
              with Vendor, which carries no storage location."

              It had stayed live on the CMO dashboard, which meant a location
              picked in the BMS view survived the switch and was still being
              applied there.
            */
            disabled={isCmoView || isCMOSelected}
            onChange={(event, newValue) => {
              setSelectedStorageLocation(newValue);
              const sloc = chosenValue(newValue?.storageLocationID);
              // Mutually exclusive with CMO, as the FSD has it. Plant is not:
              // the FSD keeps it enabled and populated with the plant holding
              // that location, so it is kept unless it no longer fits.
              if (sloc !== null) {
                setSelectedCMO({ organizationId: 'All', organizationName: 'All' });
              }
              if (!stillHeldWith("storageLocation", sloc, "plant", chosenValue(selectedPlant?.plantCode))) {
                setSelectedPlant({ plantCode: 'All' });
              }
              if (!stillHeldWith("storageLocation", sloc, "material", chosenValue(selectedMaterialCode?.materialCode))) {
                setSelectedMaterialCode({ materialCode: 'All', materialDescription: 'All' });
              }
              if (!stillHeldWith("storageLocation", sloc, "batch", chosenValue(selectedBatchNumber?.inventoryBatchNumber))) {
                setSelectedBatchNumber({ inventoryBatchNumber: 'All' });
              }
            }}
            getOptionSelected={(option, value) =>
              option.storageLocationID === value.storageLocationID
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={
                  <span style={{ whiteSpace: "nowrap" }}>
                    Select Storage Location ID
                    {/* <span style={{ color: "red" }}>*</span> */}
                  </span>
                }
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <SearchIcon style={{ marginRight: 8 }} />,
                }}
                fullWidth
              />
            )}
          />
        </Grid>

        {/* Select RM/PM Code */}

        <Grid item xs="auto" className={`${classes.smallGrid} ${classes.materialCodeGrid}`}>
          <Autocomplete
            style={{
              whiteSpace: "nowrap",
              overflowX: "auto",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
            options={filteredMaterialCodes ? [{ materialCode: 'All', materialDescription: 'All' }, ...filteredMaterialCodes] : [{ materialCode: 'All', materialDescription: 'All' }]}
            // getOptionLabel={(option) => option?.materialCode || ""}
            getOptionLabel={(option) =>
              option ? option.materialCode === 'All' ? "All" : `${option.materialCode} / ${option.materialDescription}` : ""
            }
            value={selectedMaterialCode}
            // value={filter.rmpmCodeDescription || selectedMaterialCode}
            onChange={(event, newValue) => {
              setSelectedMaterialCode(newValue);
              setSelectedBatchNumber({ inventoryBatchNumber: 'All' });
            }}
            getOptionSelected={(option, value) =>
              option.materialCode === value.materialCode && option.materialDescription === value.materialDescription
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={
                  <span style={{ whiteSpace: "nowrap" }}>
                    {localStorage.collabType === "CMO" ? "Select CMO Material Code / Description" : "Select BMS Material Code / Description"}
                  </span>
                }
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <SearchIcon style={{ marginRight: 8 }} />,
                }}
                fullWidth
              />
            )}
          />
        </Grid>

        {/* Select Sponsor Batch Number */}

        <Grid item xs="auto" className={classes.smallGrid}>
          <Autocomplete
            options={filteredBatchNumbers ? [{ inventoryBatchNumber: 'All' }, ...Array.from(new Map(filteredBatchNumbers.map(item => [item.inventoryBatchNumber, item])).values())] : [{ inventoryBatchNumber: 'All' }]}
            getOptionLabel={(option) => option?.inventoryBatchNumber || ""}
            value={selectedBatchNumber}
            // value={filter.sponsorLotNumber || selectedBatchNumber}
            onChange={(event, value) => setSelectedBatchNumber(value)}
            getOptionSelected={(option, value) =>
              option.inventoryBatchNumber === value.inventoryBatchNumber
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={
                  <span style={{ whiteSpace: "nowrap" }}>

                    {localStorage.collabType === "CMO" ? "Select CMO Batch Number" : "Select BMS Batch Number"}
                    {/* <span style={{ color: "red" }}>{" "}*</span> */}
                  </span>
                }
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <SearchIcon />,
                }}
                fullWidth
              />
            )}
          />
        </Grid>

        {/* View Report Button */}

        <Grid item xs="auto" >
          <Button className={classes.buttonStyle}
            // style={{
            //   borderRadius: "5rem",
            //   whiteSpace: "nowrap",
            //   cursor: "pointer",
            //   backgroundColor:
            //     localStorage.collabType !== "CMO" &&
            //     !selectedCMO &&
            //     !selectedPlant &&
            //     !selectedMaterialCode &&
            //     !selectedBatchNumber
            //       ? "#f4b799" 
            //       : "#ec6535",
            //   color: "#fff",
            //   textTransform: "none",
            // }}
            onClick={handleViewReport}
            disabled={
              localStorage.collabType !== "CMO" &&
              !selectedCMO &&
              !selectedBatchNumber &&
              !selectedMaterialCode &&
              !selectedPlant
            }
          >
            View Report
          </Button>
        </Grid>

        {/* Save Filters Button */}
        <Grid item xs="auto" style={{ paddingLeft: 0 }}>
          <Button className={classes.savebutton}
            // style={{
            //   borderRadius: "5rem",
            //   whiteSpace: "nowrap",
            //   cursor: "pointer",
            //   backgroundColor: "#ec6535",
            //   color: "#fff",
            //   textTransform: "none",
            // }}
            onClick={handleSaveFilters}
            disabled={isSaveDisabled}
          >
            Save Filters
          </Button>
        </Grid>
        {/* Clear All Filters -- given the buttons' padding and height so it
            sits on the same line as View Report and Save Filters. */}
        <Grid item xs="auto" style={{ paddingLeft: 0 }} onClick={handleClearFilters}>
          <div style={{ display: "flex", alignItems: "center", height: 36 }}>
          <Typography
            variant="caption"
            style={{
              fontSize: 14,
              color: "#286ACF",
              cursor: "pointer",
              textDecoration: "underline",
              whiteSpace: "nowrap",
            }}
          >
            Clear All Filters
          </Typography>
          </div>
        </Grid>
      </Grid>

      {/* A line between the filters and the options / table below. */}
      <div style={{ borderTop: "1px solid #e0e0e0", marginTop: "1rem" }} />

      {showTable && (
        <>
          <Grid item style={{ marginTop: "1rem" }}>
            {/*
              BCM-881 — the controls row above the table.

              The FSD puts the last synced date and time together with the
              Refresh action on the same line as the PER PAGE selection, to the
              right of the options. Refresh and PER PAGE used to stack on two
              lines, because the PER PAGE wrapper carried width:"100%" (which
              forced it onto a row of its own) plus marginTop:"10px"; both are
              gone and the row is now one space-between line — options at the
              left, timestamps, Refresh and PER PAGE at the right.

              Nothing about the Refresh action or the page-size Select changed,
              only where they sit.
            */}
            <Grid
              container
              justifyContent="space-between"
              alignItems="center"
              style={{ gap: "1rem" }}
            >
              {/*
                BCM-881: the two options, at the left of the row. Group rows is
                selected by default and Show total qty. only is cleared by
                default; neither changes any value, only what is displayed.
              */}
              <Grid item style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
                {bcm881Enabled && (
                  <React.Fragment>
                <label
                  className={classes.optionCard}
                  htmlFor="bcm881-group-rows"
                  style={{ cursor: "pointer", margin: 0 }}
                >
                  <Checkbox
                    id="bcm881-group-rows"
                    color="primary"
                    size="small"
                    checked={groupRows}
                    onChange={handleGroupRowsChange}
                  />
                  <span>
                    <Typography className={classes.optionLabel}>
                      Group rows
                    </Typography>
                    <Typography className={classes.optionHint}>
                      One row per material, expandable to its batches.
                    </Typography>
                  </span>
                </label>

                <Tooltip
                  title={totalQtyOnlyDisabled ? totalQtyOnlyHint : ""}
                  arrow
                  placement="top"
                  classes={tooltipClasses}
                >
                  <label
                    className={`${classes.optionCard} ${totalQtyOnlyDisabled ? classes.optionCardDisabled : ""
                      }`}
                    htmlFor="bcm881-total-qty-only"
                    style={{
                      cursor: totalQtyOnlyDisabled ? "not-allowed" : "pointer",
                      margin: 0,
                    }}
                  >
                    <Checkbox
                      id="bcm881-total-qty-only"
                      color="primary"
                      size="small"
                      checked={showTotalQtyOnly && !totalQtyOnlyDisabled}
                      disabled={totalQtyOnlyDisabled}
                      onChange={handleTotalQtyOnlyChange}
                    />
                    <span>
                      <Typography className={classes.optionLabel}>
                        Show {isCmoView ? "CMO" : "BMS"} total qty. only
                      </Typography>
                      <Typography className={classes.optionHint}>
                        {totalQtyOnlyHint}
                      </Typography>
                    </span>
                  </label>
                </Tooltip>
                  </React.Fragment>
                )}
              </Grid>

              {/*
                BCM-881: the inventory timestamps, the Refresh action and the
                PER PAGE selection, together on one line at the right of the
                row. The BMS dashboard carries a single "Last synced" line; the
                CMO dashboard carries the BMS and the CMO inventory timestamps
                on two lines in its place.
              */}
              <Grid
                item
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                  flexWrap: "wrap",
                  justifyContent: "flex-end",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                  }}
                >
                  {isCmoView ? (
                    <span style={{ textAlign: "right" }}>
                      <Typography variant="body2" style={{ whiteSpace: "nowrap" }}>
                        {`BMS inventory - ${bmsInventoryTimestamp || "NA"}`}
                      </Typography>
                      <Typography variant="body2" style={{ whiteSpace: "nowrap" }}>
                        {`CMO inventory - ${cmoInventoryTimestamp || "NA"}`}
                      </Typography>
                    </span>
                  ) : (
                    <Typography variant="body2" style={{ whiteSpace: "nowrap" }}>
                      {lastSyncedLabel}
                    </Typography>
                  )}

                  <Typography
                    variant="body2"
                    color="primary"
                    style={{
                      textDecoration: "underline",
                      fontStyle: "italic",
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                    onClick={handleRefresh}
                  >
                    Refresh
                    <Refresh color="primary" fontSize="small" />
                  </Typography>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <Typography
                    style={{
                      fontWeight: "bold",
                      whiteSpace: "nowrap",
                    }}
                  >
                    PER PAGE
                  </Typography>
                  <FormControl variant="outlined" size="small">
                    <Select
                      value={rowsPerPage}
                      onChange={handleRowsPerPageChange}
                    >
                      <MenuItem value={20}>20</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                      <MenuItem value={100}>100</MenuItem>
                      <MenuItem value={200}>200</MenuItem>
                    </Select>
                  </FormControl>
                </div>
              </Grid>
            </Grid>
          </Grid>

          <Box
            style={{
              border: "1px solid #bdbdbd",
              padding: "1rem",
              // borderRadius: "0.5rem",
              paddingBottom: "1rem",
              marginTop: "1.5rem",
            }}
          >
            <Grid
              container
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexDirection: "row",
              }}
            >
              <Grid item xs={12} sm={4}></Grid>
              <Grid item xs={12} sm={4} style={{ display: 'flex', alignItems: 'baseline', gap: "3px", justifyContent: "center" }}>
                <Typography
                  style={{
                    fontSize: "1.3rem",
                    fontWeight: 600,
                    // marginLeft: "10rem",
                    textAlign: 'center'
                  }}
                >
                  {/* BCM-881: the CMO side was already labelled "CMO Inventory
                      Status" while the BMS side rendered a bare " Inventory
                      Status" with a stray leading space. Label the BMS side too,
                      so the heading always says whose inventory is on screen.
                      inventoryToggleValue is null for a CMO login (it is seeded
                      only when collabType === "Pharma Co"), and that case still
                      renders the plain "Inventory Status" it renders today. */}
                  {`${inventoryToggleValue === "CMO"
                    ? "CMO "
                    : inventoryToggleValue === "BMS"
                      ? "BMS "
                      : ""
                    }Inventory Status`}
                </Typography>
                {/* BCM-881: the UoM conversion note, which the FSD moves off
                    the page and onto an ⓘ beside the CMO heading. */}
                {isCmoView && (
                  <InfoTip
                    title={TOOLTIP_TEXT.uomConversion}
                    className={classes.infoIcon}
                  />
                )}
                <Typography style={{
                  fontSize: "0.8rem",
                  fontWeight: 400,
                  // marginLeft: "10rem",
                  textAlign: 'center'
                }}
                >
                  {/* BCM-881: on the CMO dashboard the BMS and CMO timestamps
                      above the table replace this note (slide 6). */}
                  {inventorySyncTime && !isCmoView
                    ? ` (Last updated on ${convertToLocaleDateTime(inventorySyncTime)})`
                    : ""}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4} style={{ display: 'flex', justifyContent: 'end', gap: '10px', alignItems: 'center' }}>
                {localStorage.collabType !== "CMO" && (
                  <Button
                    variant="contained"
                    style={{
                      borderRadius: "0.5rem",
                      // backgroundColor: "#286acf",
                      // color: "#fff",
                      fontSize: "0.875rem", // Adjust font size properly
                      // width: "15rem",
                      height: "2rem",
                      textTransform: "capitalize",

                    }}
                    onClick={handleViewInventoryToggle}
                  >
                    View {inventoryToggleValue === 'BMS' ? "CMO" : "BMS"} Inventory
                  </Button>
                )}
                {localStorage.collabType !== "CMO" && screenAccess?.screenAccess && featuresAccess?.reconcileInventory && (
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    style={{
                      borderRadius: "0.5rem",
                      backgroundColor: "#286acf",
                      color: "#fff",
                      fontSize: "0.875rem", // Adjust font size properly
                      // width: "15rem",
                      height: "2rem",
                      textTransform: "capitalize"
                    }}
                    onClick={handleOpenDialog}
                  >
                    Reconcile Inventory
                  </Button>
                )}
                {/* <Button
                  variant="outlined"
                  style={{
                    borderRadius: "0.5rem",
                    borderColor: "#286acf",
                    color: "#286acf",
                    fontSize: "0.875rem",
                    height: "2rem",
                    textTransform: "capitalize"
                  }}
                  onClick={() => setReconHistoryOpen(true)}
                >
                  View Reconciliation History
                </Button> */}
              </Grid>
            </Grid>
          </Box>

          <TableContainer
            ref={tableContainerRef}
            component={Paper}
            style={{
              overflowX: "auto",
              overflowY: "auto",
              /*
                A fixed 500px left the table a short window inside a long page,
                so the page scrolled and the table scrolled at the same time.
                The measured height lets the grid use whatever is left below
                the filters, so the table's own bars are the only ones in play.
                The calc is the fallback for the first paint, before the
                measurement lands.
              */
              maxHeight: gridHeight ? `${gridHeight}px` : "calc(100vh - 320px)",
              /*
                No minHeight here. maxHeight only caps the box, so a short page
                stays as tall as its rows, but a minHeight padded a single-row
                result out to the full 260px of empty grid. The floor belongs on
                the measurement, where MIN_GRID_HEIGHT already keeps the cap
                usable on a short window.
              */
              borderLeft: "1px solid #bdbdbd",
              borderRight: "1px solid #bdbdbd",
              borderBottom: "1px solid #bdbdbd",
              width: "100%",
            }}
          >
            <Table className={classes.table} stickyHeader>
              <TableHead

              // style={{
              //   backgroundColor: "#FFE4C2 ",
              //   border: "1px solid #ffab91",
              //   height: "15px",
              // }}
              >
                <TableRow>
                  {/*
                    BCM-881 — the chevron column exists only in the grouped
                    view. Clearing "Group rows" lists every batch as its own
                    row, so there is nothing to expand and the column goes.
                  */}
                  {groupRows && (
                    <TableCell
                      style={{
                        width: "1%",
                        maxWidth: 0,
                        whiteSpace: "nowrap",
                      }}
                    />
                  )}
                  {headerCell(
                    localStorage.collabType === "CMO" ? "BMS" : "CMO",
                    "left"
                  )}
                  {/* BCM-881: CMO ID — the SAP vendor ID the stock is held
                      against. A batch held against more than one vendor ID of
                      the same CMO is listed once per vendor ID, and this is
                      the column that tells those rows apart. */}
                  {bcm881Enabled && headerCell("CMO ID", "left")}
                  {(inventoryToggleValue !== "CMO" ||
                    localStorage.collabType === "CMO") &&
                    headerCell("Plant ID", "left")}
                  {(inventoryToggleValue !== "CMO" ||
                    localStorage.collabType === "CMO") &&
                    headerCell("Storage Location ID", "left")}
                  {/* BCM-881: Brand and Material Type. */}
                  {bcm881Enabled && headerCell("Brand", "left")}
                  {bcm881Enabled && headerCell("Material Type", "left")}
                  {headerCell(
                    `${localStorage.collabType !== "CMO" ? "BMS" : "CMO"
                    } Material Code / Description`,
                    "left",
                    null,
                    null,
                    MATERIAL_CELL_STYLE
                  )}
                  {headerCell(
                    `${localStorage.collabType !== "CMO" ? "BMS" : "CMO"
                    } Batch Numbers`,
                    "left"
                  )}
                  {isCmoView && headerCell("CMO Batch Numbers", "left")}

                  {/*
                    BCM-881 — the BMS status breakup sits immediately before
                    BMS total qty., which is its sum. On the CMO dashboard the
                    BMS total stands alone here and the CMO breakup appears
                    after it, before CMO total qty.
                  */}
                  {!isCmoView &&
                    showStatusColumns &&
                    statusColumns.map((col) =>
                      headerCell(col.label, "right", null, `bms-${col.key}`)
                    )}

                  {/* BCM-881: On-hand Qty. Available is renamed BMS total qty.
                      — on the sponsor's dashboards only. A CMO login is looking
                      at its own on-hand quantity, not a BMS total, and keeps
                      both the original heading and no tooltip. */}
                  {headerCell(
                    bcm881Enabled ? "BMS Total Qty." : "On-hand Qty. Available",
                    "right",
                    bcm881Enabled ? TOOLTIP_TEXT.bmsTotalQty : null
                  )}

                  {isCmoView &&
                    showStatusColumns &&
                    statusColumns.map((col) =>
                      headerCell(col.label, "right", null, `cmo-${col.key}`)
                    )}

                  {/* BCM-881: CMO On-hand Qty. Available is renamed CMO total qty. */}
                  {isCmoView &&
                    headerCell(
                      "CMO Total Qty.",
                      "right",
                      cmoQuantityTooltips.cmoTotalQty
                    )}
                  {/* BCM-881: CMO Unrestricted Qty., reported by the CMO and
                      not part of CMO total qty. */}
                  {isCmoView &&
                    bcm881Enabled &&
                    headerCell(
                      "CMO Unrestricted Qty.",
                      "right",
                      cmoQuantityTooltips.cmoUnrestrictedQty
                    )}

                  {headerCell(
                    `${isCmoView ? "BMS " : ""}Net Qty. Available`,
                    "right",
                    !bcm881Enabled
                      ? null
                      : isCmoView
                        ? TOOLTIP_TEXT.bmsNetQtyAvailable
                        : TOOLTIP_TEXT.netQtyAvailable
                  )}
                  {headerCell(
                    `${isCmoView ? "BMS " : ""}Allocated Qty.`,
                    "right",
                    !bcm881Enabled
                      ? null
                      : isCmoView
                        ? TOOLTIP_TEXT.bmsAllocatedQty
                        : TOOLTIP_TEXT.allocatedQty
                  )}
                  {headerCell(`${isCmoView ? "BMS " : ""}UoM`, "left")}
                  {/* "The UoM, Date of Manufacturing and Expiry Date columns
                      are unchanged" — so this heading keeps its original
                      wording, lower-case "of" included. */}
                  {localStorage.collabType !== "CMO" &&
                    inventoryToggleValue === "BMS" &&
                    headerCell("Date of Manufacturing", "left")}
                  {headerCell(`${isCmoView ? "BMS " : ""}Expiry Date`, "left")}
                  {/* BCM-881: the expiry date the CMO reports, beside the BMS
                      one. Blank where none is reported, and "see batches"
                      where the BMS batch carries more than one CMO batch. */}
                  {isCmoView &&
                    bcm881Enabled &&
                    headerCell("CMO Expiry Date", "left")}
                </TableRow>
              </TableHead>
              {inventoryDataLoading === true ? (
                <TableRow style={{ height: 150 }}>
                  <TableCell colSpan={totalColumnCount}>
                    <div style={{ textAlign: "center" }}>
                      <CircularProgress color="primary" thickness={7} />
                    </div>
                  </TableCell>
                </TableRow>
              ) : /*
                    BCM-881 — the rows drawn are paginatedData, not the API's
                    totalNoOfRecords, and the two can differ: the CMO view drops
                    batches whose CMO total qty. is zero. Testing the count alone
                    took this branch and then mapped an empty array, leaving the
                    headings above a blank body with no explanation. The page the
                    user is actually looking at has to be empty too before "No
                    records found" is the right answer.
                  */
                Array.isArray(inventoryList?.inventoryDataList) &&
                inventoryList?.totalNoOfRecords > 0 &&
                paginatedData?.length > 0 ? (
                <TableBody>
                  {paginatedData?.map((vendor, index) => {
                    const lots = Array.isArray(vendor?.lotNumbersList)
                      ? vendor.lotNumbersList
                      : [];

                    /*
                      BCM-881 — the material level row exists only in the
                      grouped view. Clearing "Group rows" lists every batch as
                      its own row instead, which is the same renderer the
                      chevron already opened, so the two views cannot drift.

                      With "Group rows" selected the conditions are exactly the
                      ones this table used before: the parent row is suppressed
                      when the page holds a single material, and the batches
                      are shown when that row is expanded or when it is the
                      only material on the page.
                    */
                    const showGroupRow = groupRows && paginatedData.length !== 1;
                    const showLotRows =
                      !groupRows ||
                      (openRows[index] && lots.length > 1) ||
                      paginatedData.length === 1;

                    return (
                      <React.Fragment key={index}>
                        {showGroupRow && renderGroupRow(vendor, index, lots)}
                        {showLotRows &&
                          lots.map((lot, lotIndex) =>
                            renderLotRow(vendor, lot, `${index}-${lotIndex}`)
                          )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              ) : inventoryData?.status === false ? (
                <TableRow
                  style={{
                    border: "2px solid rgb(255,0,0)",
                    backgroundColor: "rgb(255,240,240)",
                    Opacity: 1,
                    textAlign: "center",
                    padding: "0.8rem",
                    fontSize: "0.875rem",
                  }}
                >
                  <TableCell colSpan={totalColumnCount} align="center">
                    {"Something went wrong. Please contact Schrocken Support."}
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell colSpan={totalColumnCount} align="center">
                    No records found
                  </TableCell>
                </TableRow>
              )}
            </Table>
          </TableContainer>

          {showPagination === true && inventoryDataLoading === false && (
            <Box
              style={{
                marginTop: 24,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                paddingRight: "2rem",
              }}
            >
              <IconButton
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
              >
                <ChevronLeft />
              </IconButton>
              <Typography style={{ margin: "0 16px" }}>
                Page {currentPage} of {totalPages}
              </Typography>
              <IconButton
                disabled={currentPage === totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
              >
                <ChevronRight />
              </IconButton>
            </Box>
          )}
        </>
      )}

      {openDialog && (
        <InventoryStatusPopup
          open={openDialog}
          onClose={handleCloseDialog}
          setReconDialog={setReconDialog}
          cmosList={cmosList}
          plantCodesList={plantCodesList}
          setVendorData={setVendorData}
          initialSelectedCMO={selectedCMO}
          sponsorLastUpdatedDate={inventoryData?.sponsorLatestInventorySyncTime}
        />
      )}
      <Dialog open={reconDialog} fullScreen>
        <InventoryReconciliation
          setReconDialog={setReconDialog}
          vendorData={vendorData}
        />
      </Dialog>
    </div>
  );
};

export default InventoryStatusDashboard;
